import { useState, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { hasFinancistAccess } from '../lib/roleFinancialAccess'
import type {
  TravelOrder,
  TravelOrderDailyRecord,
  SupervisorOption,
  TravelOrderDailyRecordInput,
  TravelFinancialSettings,
} from '../lib/apiPhpAdapter'
import {
  loadTravelOrders,
  loadTravelOrderDetails,
  loadTravelOrderSupervisors,
  loadTravelFinancialSettings,
  saveTravelFinancialSettings,
  createTravelOrder,
  approveTravelOrder,
  rejectTravelOrder,
  completeTravelOrder,
  approveTravelOrderSettlement,
  rejectTravelOrderSettlement,
  deleteTravelOrder,
  reportTravelOrderReturn,
  confirmTravelOrderReturn,
  downloadTravelOrderExcel,
  closeTravelOrder,
  markTravelOrderProcessed,
  unmarkTravelOrderProcessed,
  updateTravelOrderDocumentB,
  uploadFile,
  loadTravelPayrollMailSettings,
  saveTravelPayrollMailSettings,
  sendTravelPayrollToPersonal,
  downloadTravelPayrollXlsx,
  loadPersonalNumber,
} from '../lib/apiPhpAdapter'
import { SettlementSubmitConfirmModal } from '../components/SettlementSubmitConfirmModal'
import { useAlert } from '../contexts/AlertContext'
import { comparePersonNames } from '../utils/personNameSort'
import { protectedFileHref } from '../utils/fileDownload'
import './TravelOrdersView.css'

type ViewMode = 'role-select' | 'employee' | 'supervisor' | 'financist'

const CZ_MONTHS_UPPER = [
  'LEDEN',
  'ÚNOR',
  'BŘEZEN',
  'DUBEN',
  'KVĚTEN',
  'ČERVEN',
  'ČERVENEC',
  'SRPEN',
  'ZÁŘÍ',
  'ŘÍJEN',
  'LISTOPAD',
  'PROSINEC',
] as const

function defaultPayrollPeriodTitle(): string {
  const d = new Date()
  return `${CZ_MONTHS_UPPER[d.getMonth()]} ${d.getFullYear()}`
}

function formatCzDateDots(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}.${mm}.${yyyy}`
}

export default function TravelOrdersView() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkHandled = useRef<string | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const mode = new URLSearchParams(window.location.search).get('mode')
    if (mode === 'supervisor' || mode === 'employee' || mode === 'financist') {
      return mode
    }
    return 'role-select'
  })
  const [orders, setOrders] = useState<TravelOrder[]>([])
  const [loading, setLoading] = useState(() => {
    const mode = new URLSearchParams(window.location.search).get('mode')
    return mode === 'supervisor' || mode === 'employee' || mode === 'financist'
  })
  const [error, setError] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<TravelOrder | null>(null)
  const [showCompletionForm, setShowCompletionForm] = useState(false)
  const [showDocumentBEditForm, setShowDocumentBEditForm] = useState(false)

  const [payrollSelectedIds, setPayrollSelectedIds] = useState<string[]>([])
  const [payrollModalOpen, setPayrollModalOpen] = useState(false)
  const [payrollPeriodTitle, setPayrollPeriodTitle] = useState(defaultPayrollPeriodTitle)
  const [payrollPlace, setPayrollPlace] = useState('V Chebu')
  const [payrollFooterDate, setPayrollFooterDate] = useState(() => formatCzDateDots(new Date()))
  const [payrollProcessorName, setPayrollProcessorName] = useState('')
  const [payrollSending, setPayrollSending] = useState(false)
  const [payrollDownloading, setPayrollDownloading] = useState(false)

  const payrollModalBusy = payrollSending || payrollDownloading

  const payrollFormParams = () => ({
    order_ids: payrollSelectedIds,
    period_title: payrollPeriodTitle.trim(),
    place: payrollPlace.trim(),
    footer_date: payrollFooterDate.trim(),
    processor_name: payrollProcessorName.trim(),
  })

  const [settlementSubmitModalOpen, setSettlementSubmitModalOpen] = useState(false)
  const [settlementSubmitOrder, setSettlementSubmitOrder] = useState<TravelOrder | null>(null)
  const [settlementSubmitSending, setSettlementSubmitSending] = useState(false)
  const [settlementSubmitError, setSettlementSubmitError] = useState('')

  const userRole = user?.role || ''
  const isItAdmin = user?.role === 'admin' && user?.admin_type === 'spravce_it'
  const isFinancistRole = hasFinancistAccess(user)
  /** Vedoucí / ředitel / zástupce — druhá přihlášková režimová karta (schvalování). */
  const isDirectorOrVedouci = [
    'director',
    'zastupce_reditele',
    'vedouci',
    'vedouci_executor',
  ].includes(userRole)

  /** Uživatel je v seznamu schvalovatelů (nastavení IT — travel_supervisor_roles). */
  const [inSupervisorRoster, setInSupervisorRoster] = useState(false)

  const canEmployeePanel = true
  const canSupervisorPanel =
    isDirectorOrVedouci || isFinancistRole || isItAdmin || inSupervisorRoster
  const canFinancistPanel = isFinancistRole || isItAdmin

  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    loadTravelOrderSupervisors()
      .then((list) => {
        if (!cancelled) {
          setInSupervisorRoster(list.some((s) => s.user_id === user.id))
        }
      })
      .catch(() => {
        if (!cancelled) setInSupervisorRoster(false)
      })
    return () => {
      cancelled = true
    }
  }, [user?.id])

  useEffect(() => {
    if (viewMode === 'role-select') return
    loadOrdersData()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- načtení podle ?view= při přepnutí režimu
  }, [viewMode])

  // Deep-link: ?mode=supervisor&id=
  useEffect(() => {
    const mode = searchParams.get('mode')
    if (mode === 'supervisor' || mode === 'employee' || mode === 'financist') {
      if (viewMode !== mode) {
        setViewMode(mode)
      }
    }
  }, [searchParams, viewMode])

  useEffect(() => {
    const id = searchParams.get('id')
    if (!id || viewMode === 'role-select' || loading) return
    if (deepLinkHandled.current === id) return
    deepLinkHandled.current = id

    void (async () => {
      try {
        const details = await loadTravelOrderDetails(id)
        setSelectedOrder(details)
      } catch (err: any) {
        setError(err.message || 'Žádost nenalezena nebo již byla zpracována.')
      } finally {
        const next = new URLSearchParams(searchParams)
        next.delete('id')
        setSearchParams(next, { replace: true })
      }
    })()
  }, [searchParams, viewMode, loading, setSearchParams])

  const loadOrdersData = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await loadTravelOrders(
        viewMode === 'financist' ? 'financist' : viewMode === 'supervisor' ? 'supervisor' : 'employee'
      )
      setOrders(data)
      setPayrollSelectedIds((ids) => ids.filter((id) => data.some((o) => o.id === id)))
    } catch (err: any) {
      setError(err.message || 'Chyba při načítání příkazů')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectRole = (mode: ViewMode) => {
    if (mode === 'employee' && !canEmployeePanel) return
    if (mode === 'supervisor' && !canSupervisorPanel) return
    if (mode === 'financist' && !canFinancistPanel) return
    setViewMode(mode)
  }

  const handleBackToRoleSelect = () => {
    setViewMode('role-select')
    setOrders([])
    setShowCreateForm(false)
    setSelectedOrder(null)
  }

  const handleCreateOrder = () => {
    setShowCreateForm(true)
  }

  const handleOrderCreated = () => {
    setShowCreateForm(false)
    loadOrdersData()
  }

  const handleViewDetails = async (orderId: string) => {
    try {
      const details = await loadTravelOrderDetails(orderId)
      setSelectedOrder(details)
    } catch (err: any) {
      setError(err.message || 'Chyba při načítání detailů')
    }
  }

  const handleApprove = async (
    orderId: string,
    opts?: {
      estimated_total?: number | null
      advance_payment?: number | null
      advance_payment_date?: string | null
    },
  ) => {
    if (!confirm('Opravdu chcete schválit tento příkaz?')) return
    try {
      await approveTravelOrder(orderId, {
        estimated_total: opts?.estimated_total,
        advance_payment: opts?.advance_payment,
        advance_payment_date: opts?.advance_payment_date,
      })
      loadOrdersData()
      setSelectedOrder(null)
    } catch (err: any) {
      setError(err.message || 'Chyba při schvalování')
    }
  }

  const handleReject = async (orderId: string) => {
    const reason = prompt('Zadejte důvod zamítnutí:')
    if (!reason) return
    try {
      await rejectTravelOrder(orderId, reason)
      loadOrdersData()
      setSelectedOrder(null)
    } catch (err: any) {
      setError(err.message || 'Chyba při zamítání')
    }
  }

  const handleComplete = (order: TravelOrder) => {
    setSelectedOrder(order)
    setShowCompletionForm(true)
  }

  const handleOrderCompleted = async (completedOrderId: string, opts?: { draft?: boolean }) => {
    setShowCompletionForm(false)
    loadOrdersData()
    try {
      const details = await loadTravelOrderDetails(completedOrderId)
      setSelectedOrder(details)
      if (opts?.draft) {
        setError('')
      }
    } catch (err: any) {
      setSelectedOrder(null)
      setError(err.message || 'Chyba při načítání detailů po vyúčtování')
    }
  }

  const handleFinalizeSettlementFromDetail = async (order: TravelOrder) => {
    setError('')
    setSettlementSubmitError('')
    setLoading(true)
    try {
      const fresh = await loadTravelOrderDetails(order.id)
      let financialSettings: TravelFinancialSettings | null = null
      let financialSettingsLoadError: string | null = null
      try {
        financialSettings = await loadTravelFinancialSettings()
      } catch (err: any) {
        financialSettingsLoadError = err.message || 'Chyba načtení sazeb'
      }
      const validationErr = validateSettlementReadyForSubmit(
        fresh,
        financialSettings,
        financialSettingsLoadError,
      )
      if (validationErr) {
        setError(validationErr)
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      setSettlementSubmitOrder(fresh)
      setSettlementSubmitModalOpen(true)
    } catch (err: any) {
      setError(err.message || 'Chyba při načítání vyúčtování před odesláním')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } finally {
      setLoading(false)
    }
  }

  const executeFinalizeSettlementToSupervisor = async () => {
    const order = settlementSubmitOrder
    if (!order) return
    setError('')
    setSettlementSubmitError('')
    setSettlementSubmitSending(true)
    setLoading(true)
    try {
      // Vždy načíst čerstvá data z API — jinak může být selectedOrder bez daily_records (race po uložení konceptu)
      // a server smaže řádky vyúčtování a uloží prázdnou tabulku.
      const fresh = await loadTravelOrderDetails(order.id)
      let financialSettings: TravelFinancialSettings | null = null
      let financialSettingsLoadError: string | null = null
      try {
        financialSettings = await loadTravelFinancialSettings()
      } catch (err: any) {
        financialSettingsLoadError = err.message || 'Chyba načtení sazeb'
      }
      const validationErr = validateSettlementReadyForSubmit(
        fresh,
        financialSettings,
        financialSettingsLoadError,
      )
      if (validationErr) {
        setSettlementSubmitError(validationErr)
        return
      }
      await completeTravelOrder(order.id, travelOrderToCompletePayload(fresh))
      setSettlementSubmitModalOpen(false)
      setSettlementSubmitOrder(null)
      setSettlementSubmitError('')
      await handleOrderCompleted(order.id)
    } catch (err: any) {
      setSettlementSubmitError(err.message || 'Chyba při odesílání vyúčtování')
    } finally {
      setSettlementSubmitSending(false)
      setLoading(false)
    }
  }

  const handleDelete = async (orderId: string) => {
    if (!confirm('Opravdu chcete smazat tento příkaz?')) return
    try {
      await deleteTravelOrder(orderId)
      loadOrdersData()
    } catch (err: any) {
      setError(err.message || 'Chyba při mazání')
    }
  }

  const handleDownloadExcel = async (order: TravelOrder, part: 'a' | 'b' = 'a') => {
    try {
      setError('')
      await downloadTravelOrderExcel(order.id, order.order_number || order.id, part)
    } catch (err: any) {
      setError(err.message || 'Chyba při stahování Excelu')
    }
  }

  const handleReportReturn = async (orderId: string) => {
    if (!confirm('Nahlásit návrat ze služební cesty a pokračovat k vyúčtování?')) return
    try {
      await reportTravelOrderReturn(orderId)
      const details = await loadTravelOrderDetails(orderId)
      setSelectedOrder(details)
      loadOrdersData()
    } catch (err: any) {
      setError(err.message || 'Chyba')
    }
  }

  const handleConfirmReturn = async (orderId: string) => {
    if (!confirm('Potvrdit, že zaměstnanec se vrátil? Tím se zpřístupní vyúčtování.')) return
    try {
      await confirmTravelOrderReturn(orderId)
      const details = await loadTravelOrderDetails(orderId)
      setSelectedOrder(details)
      loadOrdersData()
    } catch (err: any) {
      setError(err.message || 'Chyba')
    }
  }

  const handleApproveSettlement = async (orderId: string) => {
    if (!confirm('Potvrdit vyúčtování bez úprav a předat je hlavnímu ekonomovi?')) return
    setError('')
    try {
      await approveTravelOrderSettlement(orderId)
      const details = await loadTravelOrderDetails(orderId)
      setSelectedOrder(details)
      loadOrdersData()
    } catch (err: any) {
      setError(err.message || 'Chyba při potvrzování vyúčtování')
    }
  }

  const handleRejectSettlement = async (orderId: string) => {
    const note = window.prompt('Zpráva pro zaměstnance (povinná) — proč vracíte vyúčtování k úpravě:')
    if (note == null || !String(note).trim()) return
    setError('')
    try {
      await rejectTravelOrderSettlement(orderId, String(note).trim())
      const details = await loadTravelOrderDetails(orderId)
      setSelectedOrder(details)
      loadOrdersData()
    } catch (err: any) {
      setError(err.message || 'Chyba při vracení vyúčtování')
    }
  }

  const handleCloseFinancist = async (orderId: string) => {
    const notes = window.prompt('Poznámka ekonoma (volitelné):') ?? ''
    try {
      await closeTravelOrder(orderId, notes || undefined)
      setSelectedOrder(null)
      loadOrdersData()
    } catch (err: any) {
      setError(err.message || 'Chyba při uzavírání')
    }
  }

  const handleToggleFinancistProcessed = async (order: TravelOrder) => {
    const isProcessed = !!order.financist_processed_at
    try {
      if (isProcessed) {
        await unmarkTravelOrderProcessed(order.id)
      } else {
        await markTravelOrderProcessed(order.id)
      }
      const nextAt = isProcessed ? null : new Date().toISOString()
      const nextBy = isProcessed ? null : user?.id ?? null
      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id
            ? { ...o, financist_processed_at: nextAt, financist_processed_by: nextBy }
            : o,
        ),
      )
      if (selectedOrder?.id === order.id) {
        setSelectedOrder({
          ...selectedOrder,
          financist_processed_at: nextAt,
          financist_processed_by: nextBy,
        })
      }
    } catch (err: any) {
      setError(
        err.message ||
          (isProcessed ? 'Chyba při rušení označení zpracování' : 'Chyba: nelze označit jako zpracováno'),
      )
    }
  }

  const handleOpenDocumentBEdit = () => {
    setShowDocumentBEditForm(true)
  }

  const handleDocumentBEditSaved = async () => {
    if (!selectedOrder) return
    try {
      const details = await loadTravelOrderDetails(selectedOrder.id)
      setSelectedOrder(details)
      setShowDocumentBEditForm(false)
      loadOrdersData()
    } catch (err: any) {
      setError(err.message || 'Chyba při načítání detailů')
    }
  }


  if (viewMode === 'role-select') {
    return (
      <div className="travel-orders-container">
        <div className="travel-orders-header">
          <div className="travel-icon">✈️</div>
          <h1>CESTOVNÍ PŘÍKAZ</h1>
          <p className="travel-subtitle">Správa cestovních příkazů</p>
          <p className="travel-orders-home-note" style={{ maxWidth: 520, margin: '0.25rem auto 0', fontSize: '0.9rem', lineHeight: 1.4, color: 'rgba(0,0,0,0.75)' }}>
            Chcete-li náhradu výdajů na stravné, musí každý spolucestující vyplnit vlastní cestovní příkaz.
          </p>
        </div>

        <p className="role-select-hint">Vyberte roli — u některých účtů nejsou všechny režimy dostupné.</p>

        <div className="role-select-cards" role="group" aria-label="Režim cestovního příkazu">
          <button
            type="button"
            className="role-card"
            onClick={() => handleSelectRole('employee')}
          >
            <span className="role-icon" aria-hidden>👤</span>
            <span className="role-card-title">1. Pracovník</span>
            <span className="role-card-desc">Nový příkaz a vlastní příkazy</span>
          </button>

          <button
            type="button"
            className={`role-card${!canSupervisorPanel ? ' role-card--disabled' : ''}`}
            disabled={!canSupervisorPanel}
            onClick={() => handleSelectRole('supervisor')}
            title={
              !canSupervisorPanel
                ? 'Dostupné řediteli, zástupci, vedoucím, ekonomovi, správci IT nebo účtu zařazenému mezi schvalovatele v administraci.'
                : undefined
            }
          >
            <span className="role-icon" aria-hidden>👔</span>
            <span className="role-card-title">2. Vedoucí nebo ředitel</span>
            <span className="role-card-desc">Schválení příkazu, vyúčtování, potvrzení návratu</span>
          </button>

          <button
            type="button"
            className={`role-card${!canFinancistPanel ? ' role-card--disabled' : ''}`}
            disabled={!canFinancistPanel}
            onClick={() => handleSelectRole('financist')}
            title={!canFinancistPanel ? 'Tento režim je dostupný ekonomovi nebo správci IT.' : undefined}
          >
            <span className="role-icon" aria-hidden>💼</span>
            <span className="role-card-title">3. Ekonom</span>
            <span className="role-card-desc">Všechny příkazy, uzavření vyúčtování</span>
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="travel-orders-container">
      <div className="travel-orders-toolbar">
        <button className="btn-back" onClick={handleBackToRoleSelect}>
          ← Zpět na výběr role
        </button>
        {viewMode === 'employee' && !showCreateForm && (
          <button className="btn-primary" onClick={handleCreateOrder}>
            ➕ Nový příkaz
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {viewMode === 'financist' && !showCreateForm && <TravelFinancialSettingsPanel />}

      {showCreateForm && viewMode === 'employee' ? (
        <CreateTravelOrderForm 
          onSuccess={handleOrderCreated}
          onCancel={() => setShowCreateForm(false)}
        />
      ) : showCompletionForm && selectedOrder ? (
        <CompleteTravelOrderForm
          order={selectedOrder}
          settlementEditor={
            viewMode === 'financist'
              ? 'financist'
              : viewMode === 'supervisor' && selectedOrder.status === 'pending_settlement_supervisor'
                ? 'supervisor'
                : 'employee'
          }
          onSuccess={handleOrderCompleted}
          onCancel={() => {
            setShowCompletionForm(false)
            setSelectedOrder(null)
          }}
        />
      ) : showDocumentBEditForm && selectedOrder ? (
        <EditDocumentBForm
          order={selectedOrder}
          onCancel={() => setShowDocumentBEditForm(false)}
          onSuccess={handleDocumentBEditSaved}
        />
      ) : selectedOrder ? (
        <TravelOrderDetails
          order={selectedOrder}
          viewMode={viewMode}
          canCloseCompleted={
            viewMode === 'financist' &&
            (hasFinancistAccess(user) || user?.role === 'director' || isItAdmin)
          }
          onClose={() => setSelectedOrder(null)}
          onApprove={(opts) => handleApprove(selectedOrder.id, opts)}
          onReject={() => handleReject(selectedOrder.id)}
          onComplete={() => handleComplete(selectedOrder)}
          onFinalizeSettlement={() => void handleFinalizeSettlementFromDetail(selectedOrder)}
          onDownloadExcelA={() => handleDownloadExcel(selectedOrder, 'a')}
          onDownloadExcelB={() => handleDownloadExcel(selectedOrder, 'b')}
          onReportReturn={() => handleReportReturn(selectedOrder.id)}
          onConfirmReturn={() => handleConfirmReturn(selectedOrder.id)}
          onApproveSettlement={() => handleApproveSettlement(selectedOrder.id)}
          onRejectSettlement={() => handleRejectSettlement(selectedOrder.id)}
          onCloseFinancist={() => handleCloseFinancist(selectedOrder.id)}
          onToggleFinancistProcessed={() => handleToggleFinancistProcessed(selectedOrder)}
          onEditDocumentB={handleOpenDocumentBEdit}
        />
      ) : (
        <TravelOrdersList
          orders={orders}
          loading={loading}
          viewMode={viewMode}
          onViewDetails={handleViewDetails}
          onDelete={handleDelete}
          isItAdmin={isItAdmin}
          onToggleFinancistProcessed={handleToggleFinancistProcessed}
          payrollSelectedIds={payrollSelectedIds}
          onTogglePayrollSelect={(orderId, checked) => {
            setPayrollSelectedIds((prev) => {
              if (checked) return prev.includes(orderId) ? prev : [...prev, orderId]
              return prev.filter((x) => x !== orderId)
            })
          }}
          onSelectAllClosedPayroll={(ids) => setPayrollSelectedIds(ids)}
          onOpenPayrollSend={() => {
            setPayrollPeriodTitle(defaultPayrollPeriodTitle())
            setPayrollFooterDate(formatCzDateDots(new Date()))
            setPayrollPlace('V Chebu')
            setPayrollProcessorName((user?.name || '').trim())
            setPayrollModalOpen(true)
          }}
        />
      )}

      <SettlementSubmitConfirmModal
        isOpen={settlementSubmitModalOpen}
        orderNumber={settlementSubmitOrder?.order_number}
        loading={settlementSubmitSending}
        errorMessage={settlementSubmitError || undefined}
        onClose={() => {
          if (settlementSubmitSending) return
          setSettlementSubmitModalOpen(false)
          setSettlementSubmitOrder(null)
          setSettlementSubmitError('')
        }}
        onConfirm={() => void executeFinalizeSettlementToSupervisor()}
      />

      {payrollModalOpen && (
        <div
          className="travel-payroll-modal-overlay"
          role="presentation"
          onClick={() => {
            if (!payrollModalBusy) setPayrollModalOpen(false)
          }}
        >
          <div
            className="travel-payroll-modal"
            role="dialog"
            aria-labelledby="travel-payroll-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="travel-payroll-modal-title">Odeslat předpis výplat personálnímu oddělení</h3>
            <p className="field-hint">
              Vyplňte údaje předpisu. Tlačítkem <strong>Stáhnout náhled</strong> si ekonom může zkontrolovat vzhled
              Excelu; odeslání e-mailem použije stejný soubor na adresy z nastavení výše ({payrollSelectedIds.length}{' '}
              uzavřených příkazů).
            </p>
            <div className="form-group">
              <label htmlFor="payroll-period">Období (nadpis souboru)</label>
              <input
                id="payroll-period"
                type="text"
                value={payrollPeriodTitle}
                onChange={(e) => setPayrollPeriodTitle(e.target.value)}
                placeholder="např. DUBEN 2026"
              />
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="payroll-place">Místo (zápatí)</label>
                <input
                  id="payroll-place"
                  type="text"
                  value={payrollPlace}
                  onChange={(e) => setPayrollPlace(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label htmlFor="payroll-fdate">Datum (zápatí)</label>
                <input
                  id="payroll-fdate"
                  type="text"
                  value={payrollFooterDate}
                  onChange={(e) => setPayrollFooterDate(e.target.value)}
                  placeholder="dd.mm.rrrr"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="payroll-proc">Zpracoval</label>
              <input
                id="payroll-proc"
                type="text"
                value={payrollProcessorName}
                onChange={(e) => setPayrollProcessorName(e.target.value)}
                placeholder="jméno ekonoma"
              />
            </div>
            <div className="travel-payroll-modal__actions">
              <button
                type="button"
                className="btn-secondary"
                disabled={payrollModalBusy}
                onClick={() => setPayrollModalOpen(false)}
              >
                Zrušit
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={payrollModalBusy || payrollSelectedIds.length === 0 || !payrollPeriodTitle.trim()}
                onClick={async () => {
                  setPayrollDownloading(true)
                  setError('')
                  try {
                    await downloadTravelPayrollXlsx(payrollFormParams())
                  } catch (err: unknown) {
                    setError(err instanceof Error ? err.message : 'Chyba při stahování předpisu')
                  } finally {
                    setPayrollDownloading(false)
                  }
                }}
              >
                {payrollDownloading ? 'Generování…' : '📥 Stáhnout náhled (Excel)'}
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={payrollModalBusy || payrollSelectedIds.length === 0 || !payrollPeriodTitle.trim()}
                onClick={async () => {
                  setPayrollSending(true)
                  setError('')
                  try {
                    await sendTravelPayrollToPersonal(payrollFormParams())
                    setPayrollModalOpen(false)
                    setPayrollSelectedIds([])
                    await loadOrdersData()
                  } catch (err: unknown) {
                    setError(err instanceof Error ? err.message : 'Odeslání se nezdařilo')
                  } finally {
                    setPayrollSending(false)
                  }
                }}
              >
                {payrollSending ? 'Odesílání…' : 'Vygenerovat a odeslat e-mailem'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================
// Nastavení sazeb (ekonom / IT)
// ============================================

function TravelFinancialSettingsPanel() {
  const [settings, setSettings] = useState<TravelFinancialSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [draft, setDraft] = useState<Partial<TravelFinancialSettings>>({})
  const [draftText, setDraftText] = useState<Record<string, string>>({})

  const [payrollEmails, setPayrollEmails] = useState('')
  const [payrollMailLoading, setPayrollMailLoading] = useState(true)
  const [payrollMailSaving, setPayrollMailSaving] = useState(false)
  const [payrollMailMsg, setPayrollMailMsg] = useState<string | null>(null)
  const [payrollMailErr, setPayrollMailErr] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    loadTravelPayrollMailSettings()
      .then((r) => {
        if (!cancelled) setPayrollEmails(r.mail_recipients || '')
      })
      .catch(() => {
        if (!cancelled) setPayrollEmails('')
      })
      .finally(() => {
        if (!cancelled) setPayrollMailLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    loadTravelFinancialSettings()
      .then((s) => {
        if (!cancelled) {
          setSettings(s)
          setDraft(s)
          const t: Record<string, string> = {}
          const anyS = s as unknown as Record<string, unknown>
          for (const k of Object.keys(anyS)) {
            t[k] = anyS[k] === null || anyS[k] === undefined ? '' : String(anyS[k])
          }
          setDraftText(t)
        }
      })
      .catch((e: Error) => {
        if (!cancelled) setErr(e.message || 'Chyba načtení sazeb')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setErr(null)
    setMsg(null)
    try {
      const r = await saveTravelFinancialSettings(draft as TravelFinancialSettings)
      setSettings(r.settings)
      setDraft(r.settings)
      const t: Record<string, string> = {}
      const anyS = r.settings as unknown as Record<string, unknown>
      for (const k of Object.keys(anyS)) {
        t[k] = anyS[k] === null || anyS[k] === undefined ? '' : String(anyS[k])
      }
      setDraftText(t)
      setMsg('Nastavení uloženo.')
    } catch (e: unknown) {
      const m = e instanceof Error ? e.message : 'Uložení se nezdařilo'
      setErr(m)
    } finally {
      setSaving(false)
    }
  }

  const numField = (k: keyof TravelFinancialSettings) => (
    <input
      type="text"
      inputMode="decimal"
      className="travel-fin-settings-input"
      value={draftText[String(k)] ?? ''}
      onChange={(e) => {
        const txt = e.target.value
        setDraftText((t) => ({ ...t, [String(k)]: txt }))

        const raw = txt.replace(',', '.').trim()
        if (
          raw === '' ||
          raw === '-' ||
          raw === '.' ||
          raw === '-.' ||
          raw.endsWith('.')
        ) {
          return
        }
        const n = parseFloat(raw)
        if (Number.isFinite(n)) {
          setDraft((d) => ({ ...d, [k]: n }))
        }
      }}
      onBlur={() => {
        const txt = (draftText[String(k)] ?? '').trim()
        if (txt === '' || txt === '-' || txt === '.' || txt === '-.') {
          setDraftText((t) => ({ ...t, [String(k)]: '' }))
          setDraft((d) => ({ ...d, [k]: 0 }))
          return
        }
        const raw = txt.replace(',', '.').trim()
        const n = parseFloat(raw)
        if (!Number.isFinite(n)) {
          setDraftText((t) => ({ ...t, [String(k)]: '' }))
          setDraft((d) => ({ ...d, [k]: 0 }))
          return
        }
        setDraftText((t) => ({ ...t, [String(k)]: String(n) }))
        setDraft((d) => ({ ...d, [k]: n }))
      }}
    />
  )

  return (
    <div className="travel-financial-settings-panel">
      <details className="travel-fin-settings-spoiler">
        <summary>Sazby PHM a náhrady (nastavení ekonomem)</summary>
        <div className="travel-fin-settings-spoiler__body">
          <p className="field-hint">
            Tyto hodnoty používá server při vyúčtování zaměstnance (náhrada za km, cena PHM dle druhu paliva,
            sazby stravného (tuzemské / zahraniční dle délky cesty). Upravuje je hlavní ekonom nebo správce IT.
          </p>
          {loading && <p>Načítání…</p>}
          {err && <div className="error-message">{err}</div>}
          {msg && <div className="travel-fin-settings-ok">{msg}</div>}
          {!loading && settings && (
            <form onSubmit={onSave} className="travel-fin-settings-form">
          <div className="form-grid">
            <div className="form-group">
              <label>Základní sazba za 1 km — osobní auta (Kč/km)</label>
              {numField('mileage_compensation_kc_per_km')}
            </div>
            <div className="form-group">
              <label>Benzín 95 (Kč/l)</label>
              {numField('fuel_benzin_95_kc_per_l')}
            </div>
            <div className="form-group">
              <label>Benzín 98 (Kč/l)</label>
              {numField('fuel_benzin_98_kc_per_l')}
            </div>
            <div className="form-group">
              <label>Nafta (Kč/l)</label>
              {numField('fuel_nafta_kc_per_l')}
            </div>
            <div className="form-group">
              <label>Elektřina (Kč/kWh)</label>
              {numField('fuel_elektrina_kc_per_kwh')}
            </div>
          </div>
          <h4 style={{ marginTop: 20, marginBottom: 8 }}>Tuzemské stravné (CZK)</h4>
          <div className="form-grid">
            <div className="form-group">
              <label>do 5 hodin</label>
              {numField('tuzemske_stravne_kc_upto_5h')}
            </div>
            <div className="form-group">
              <label>od 5 do 12 hodin</label>
              {numField('tuzemske_stravne_kc_5_to_12h')}
            </div>
            <div className="form-group">
              <label>od 12 do 18 hodin</label>
              {numField('tuzemske_stravne_kc_12_to_18h')}
            </div>
            <div className="form-group">
              <label>nad 18 hodin</label>
              {numField('tuzemske_stravne_kc_over_18h')}
            </div>
          </div>

          <h4 style={{ marginTop: 20, marginBottom: 8 }}>Zahraniční stravné (EUR)</h4>
          <div className="form-grid">
            <div className="form-group">
              <label>do 5 hodin</label>
              {numField('zahranicni_stravne_eur_upto_5h')}
            </div>
            <div className="form-group">
              <label>od 5 do 12 hodin</label>
              {numField('zahranicni_stravne_eur_5_to_12h')}
            </div>
            <div className="form-group">
              <label>od 12 do 18 hodin</label>
              {numField('zahranicni_stravne_eur_12_to_18h')}
            </div>
            <div className="form-group">
              <label>nad 18 hodin</label>
              {numField('zahranicni_stravne_eur_over_18h')}
            </div>
            <div className="form-group">
              <label>Kurz EUR → Kč</label>
              {numField('eur_to_czk')}
            </div>
          </div>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Ukládání…' : 'Uložit sazby'}
          </button>
            </form>
          )}
        </div>
      </details>
      <details className="travel-fin-settings-spoiler">
        <summary>E-maily pro personální oddělení (předpis výplat náhrad)</summary>
        <div className="travel-fin-settings-spoiler__body">
          <p className="field-hint">
            Adresy pro zaslání souboru Excel „Předpis výplat cestovních náhrad“ po výběru uzavřených příkazů v seznamu.
            Oddělte čárkou nebo středníkem.
          </p>
          {payrollMailLoading ? (
            <p>Načítání e-mailů…</p>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault()
                setPayrollMailSaving(true)
                setPayrollMailErr(null)
                setPayrollMailMsg(null)
                try {
                  await saveTravelPayrollMailSettings(payrollEmails)
                  setPayrollMailMsg('Adresy uloženy.')
                } catch (er: unknown) {
                  setPayrollMailErr(er instanceof Error ? er.message : 'Uložení se nezdařilo')
                } finally {
                  setPayrollMailSaving(false)
                }
              }}
            >
              {payrollMailErr && <div className="error-message">{payrollMailErr}</div>}
              {payrollMailMsg && <div className="travel-fin-settings-ok">{payrollMailMsg}</div>}
              <div className="form-group form-group--wide">
                <label htmlFor="travel-payroll-mails">Příjemci (personální oddělení)</label>
                <textarea
                  id="travel-payroll-mails"
                  rows={2}
                  value={payrollEmails}
                  onChange={(e) => setPayrollEmails(e.target.value)}
                  placeholder="např. personal@skola.cz, ucetni@skola.cz"
                  className="travel-fin-settings-input"
                />
              </div>
              <button type="submit" className="btn-secondary" disabled={payrollMailSaving}>
                {payrollMailSaving ? 'Ukládání…' : 'Uložit e-maily'}
              </button>
            </form>
          )}
        </div>
      </details>
    </div>
  )
}

// ============================================
// СПИСОК ПРИКАЗОВ
// ============================================

interface TravelOrdersListProps {
  orders: TravelOrder[]
  loading: boolean
  viewMode: ViewMode
  onViewDetails: (orderId: string) => void
  onDelete: (orderId: string) => void
  isItAdmin: boolean
  onToggleFinancistProcessed?: (order: TravelOrder) => void
  payrollSelectedIds?: string[]
  onTogglePayrollSelect?: (orderId: string, selected: boolean) => void
  /** Předá ID uzavřených příkazů odpovídajících aktuálnímu filtru/seznamu. */
  onSelectAllClosedPayroll?: (closedOrderIds: string[]) => void
  onOpenPayrollSend?: () => void
}

type TravelListSortKey =
  | 'departure_date'
  | 'created_at'
  | 'order_number'
  | 'employee_name'
  | 'destination_place'
  | 'status'

const TRAVEL_ORDER_STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'draft', label: 'Koncept' },
  { value: 'pending_approval', label: 'Čeká na schválení' },
  { value: 'approved', label: 'Schváleno' },
  { value: 'rejected', label: 'Zamítnuto' },
  { value: 'in_progress', label: 'Probíhá' },
  { value: 'pending_return_confirmation', label: 'Čeká na potvrzení návratu' },
  { value: 'pending_completion', label: 'Čeká na vyúčtování' },
  {
    value: 'pending_settlement_supervisor',
    label: 'Čeká na schválení vyúčtování (vedoucí)',
  },
  { value: 'completed', label: 'Dokončeno' },
  { value: 'closed', label: 'Uzavřeno' },
]

const TRAVEL_LIST_SORT_OPTIONS: { key: TravelListSortKey; label: string }[] = [
  { key: 'departure_date', label: 'Datum odjezdu' },
  { key: 'created_at', label: 'Datum vytvoření' },
  { key: 'order_number', label: 'Číslo příkazu' },
  { key: 'employee_name', label: 'Pracovník' },
  { key: 'destination_place', label: 'Místo' },
  { key: 'status', label: 'Stav' },
]

function TravelOrdersList({
  orders,
  loading,
  viewMode,
  onViewDetails,
  onDelete,
  isItAdmin,
  onToggleFinancistProcessed,
  payrollSelectedIds = [],
  onTogglePayrollSelect,
  onSelectAllClosedPayroll,
  onOpenPayrollSend,
}: TravelOrdersListProps) {
  const showPayrollPick = viewMode === 'financist' && onTogglePayrollSelect && onOpenPayrollSend
  const showProcessedCol = viewMode === 'financist' && !!onToggleFinancistProcessed
  const showListFilters =
    viewMode === 'employee' || viewMode === 'supervisor' || viewMode === 'financist'
  const [listSearch, setListSearch] = useState('')
  const [listStatusFilter, setListStatusFilter] = useState<string>('all')
  const [listProcessedFilter, setListProcessedFilter] = useState<'all' | 'processed' | 'unprocessed'>('all')
  const [listSortKey, setListSortKey] = useState<TravelListSortKey>('departure_date')
  const [listSortDir, setListSortDir] = useState<'asc' | 'desc'>('desc')

  const processedOrders = useMemo(() => {
    if (!showListFilters) {
      return orders
    }
    let list = [...orders]
    if (listStatusFilter !== 'all') {
      list = list.filter((o) => o.status === listStatusFilter)
    }
    if (showProcessedCol && listProcessedFilter === 'processed') {
      list = list.filter((o) => !!o.financist_processed_at)
    } else if (showProcessedCol && listProcessedFilter === 'unprocessed') {
      list = list.filter((o) => !o.financist_processed_at)
    }
    const q = listSearch.trim().toLowerCase()
    if (q) {
      list = list.filter((o) => {
        const hay = [
          o.order_number,
          o.employee_name,
          o.purpose,
          o.destination_place,
          o.destination_country,
        ]
          .map((x) => String(x ?? '').toLowerCase())
          .join(' ')
        return hay.includes(q)
      })
    }
    const dir = listSortDir === 'asc' ? 1 : -1
    list.sort((a, b) => {
      const cmpStr = (x: string | undefined, y: string | undefined) =>
        (x || '').localeCompare(y || '', 'cs', { sensitivity: 'base' })
      switch (listSortKey) {
        case 'departure_date':
          return cmpStr(a.departure_date, b.departure_date) * dir
        case 'created_at':
          return cmpStr(a.created_at, b.created_at) * dir
        case 'employee_name':
          return comparePersonNames({ name: a.employee_name }, { name: b.employee_name }) * dir
        case 'destination_place': {
          const da = [a.destination_place, a.destination_country].filter(Boolean).join(', ')
          const db = [b.destination_place, b.destination_country].filter(Boolean).join(', ')
          return cmpStr(da, db) * dir
        }
        case 'status':
          return cmpStr(a.status, b.status) * dir
        case 'order_number': {
          const na = parseInt(String(a.order_number).replace(/\D/g, ''), 10)
          const nb = parseInt(String(b.order_number).replace(/\D/g, ''), 10)
          if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) {
            return (na - nb) * dir
          }
          return cmpStr(String(a.order_number), String(b.order_number)) * dir
        }
        default:
          return 0
      }
    })
    return list
  }, [
    orders,
    showListFilters,
    showProcessedCol,
    listSearch,
    listStatusFilter,
    listProcessedFilter,
    listSortKey,
    listSortDir,
  ])

  const listFiltersActive =
    showListFilters &&
    (listSearch.trim() !== '' ||
      listStatusFilter !== 'all' ||
      (showProcessedCol && listProcessedFilter !== 'all'))

  const resetListFilters = () => {
    setListSearch('')
    setListStatusFilter('all')
    setListProcessedFilter('all')
    setListSortKey('departure_date')
    setListSortDir('desc')
  }

  const tableColSpan = 7 + (showPayrollPick ? 1 : 0) + (showProcessedCol ? 1 : 0)

  const getStatusBadge = (order: TravelOrder) => {
    const status = order.status
    if (status === 'closed' && order.payroll_sent_at) {
      const sent = order.payroll_sent_at
      const tip =
        sent && !Number.isNaN(Date.parse(sent))
          ? `Odesláno personálnímu: ${new Date(sent).toLocaleString('cs-CZ')}`
          : 'Odesláno personálnímu oddělení'
      return (
        <span className="status-badge status-closed status-payroll-sent" title={tip}>
          Uzavřeno · odesláno PO
        </span>
      )
    }
    const statusMap: Record<string, { label: string; className: string }> = {
      draft: { label: 'Koncept', className: 'status-draft' },
      pending_approval: { label: 'Čeká na schválení', className: 'status-pending' },
      approved: { label: 'Schváleno', className: 'status-approved' },
      rejected: { label: 'Zamítnuto', className: 'status-rejected' },
      in_progress: { label: 'Probíhá', className: 'status-in-progress' },
      pending_return_confirmation: { label: 'Čeká na potvrzení návratu', className: 'status-pending-return' },
      pending_completion: { label: 'Čeká na vyúčtování', className: 'status-pending-completion' },
      pending_settlement_supervisor: {
        label: 'Čeká na schválení vyúčtování (vedoucí)',
        className: 'status-pending-settlement',
      },
      completed: { label: 'Dokončeno', className: 'status-completed' },
      closed: { label: 'Uzavřeno', className: 'status-closed' },
    }
    const info = statusMap[status] || { label: status, className: 'status-unknown' }
    return <span className={`status-badge ${info.className}`}>{info.label}</span>
  }

  if (loading) {
    return <div className="loading">Načítání...</div>
  }

  if (orders.length === 0) {
    return <div className="no-orders">Žádné cestovní příkazy</div>
  }

  return (
    <div className="orders-list">
      <h2>
        {viewMode === 'employee' && 'Moje příkazy'}
        {viewMode === 'supervisor' && 'Příkazy k schválení'}
        {viewMode === 'financist' && 'Všechny příkazy'}
      </h2>

      {showListFilters && (
        <div className="travel-orders-list-toolbar" role="search">
          <div className="travel-orders-list-toolbar__grid">
            <label className="travel-orders-list-toolbar__field">
              <span className="travel-orders-list-toolbar__label">Hledat</span>
              <input
                type="search"
                className="travel-orders-list-toolbar__input"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                placeholder="Číslo, jméno, účel, místo…"
                autoComplete="off"
              />
            </label>
            <label className="travel-orders-list-toolbar__field">
              <span className="travel-orders-list-toolbar__label">Stav</span>
              <select
                className="travel-orders-list-toolbar__select"
                value={listStatusFilter}
                onChange={(e) => setListStatusFilter(e.target.value)}
              >
                <option value="all">Všechny</option>
                {TRAVEL_ORDER_STATUS_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            {showProcessedCol && (
              <label className="travel-orders-list-toolbar__field">
                <span className="travel-orders-list-toolbar__label">Zpracování</span>
                <select
                  className="travel-orders-list-toolbar__select"
                  value={listProcessedFilter}
                  onChange={(e) =>
                    setListProcessedFilter(e.target.value as 'all' | 'processed' | 'unprocessed')
                  }
                >
                  <option value="all">Všechny</option>
                  <option value="unprocessed">Nezpracované</option>
                  <option value="processed">Zpracované</option>
                </select>
              </label>
            )}
            <label className="travel-orders-list-toolbar__field">
              <span className="travel-orders-list-toolbar__label">Řadit podle</span>
              <select
                className="travel-orders-list-toolbar__select"
                value={listSortKey}
                onChange={(e) => setListSortKey(e.target.value as TravelListSortKey)}
              >
                {TRAVEL_LIST_SORT_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="travel-orders-list-toolbar__field">
              <span className="travel-orders-list-toolbar__label">Pořadí</span>
              <select
                className="travel-orders-list-toolbar__select"
                value={listSortDir}
                onChange={(e) => setListSortDir(e.target.value as 'asc' | 'desc')}
              >
                <option value="asc">Vzestupně</option>
                <option value="desc">Sestupně</option>
              </select>
            </label>
            {listFiltersActive && (
              <div className="travel-orders-list-toolbar__actions">
                <button type="button" className="btn-secondary travel-orders-list-toolbar__reset" onClick={resetListFilters}>
                  Zrušit filtry
                </button>
              </div>
            )}
          </div>
          {showPayrollPick && (
            <div className="travel-orders-payroll-toolbar">
              <button
                type="button"
                className="btn-secondary"
                onClick={() =>
                  onSelectAllClosedPayroll?.(
                    processedOrders
                      .filter((o) => o.status === 'closed' && !o.payroll_sent_at)
                      .map((o) => o.id),
                  )
                }
              >
                Vybrat všechny uzavřené ve filtrovaném seznamu
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={payrollSelectedIds.length === 0}
                onClick={() => onOpenPayrollSend?.()}
              >
                Odeslat předpis personálnímu… ({payrollSelectedIds.length})
              </button>
              <span className="field-hint travel-orders-payroll-toolbar__hint">
                Zaškrtněte uzavřené příkazy — po uzavření ekonem jsou připravené zařadit do měsíčního předpisu výplat.
              </span>
            </div>
          )}
          <p className="travel-orders-list-toolbar__hint">
            Zobrazeno {processedOrders.length} z {orders.length} příkazů
          </p>
        </div>
      )}

      <table className="orders-table">
        <thead>
          <tr>
            {showPayrollPick ? <th className="travel-orders-table__check">Předpis</th> : null}
            <th>Číslo</th>
            <th>Pracovník</th>
            <th>Účel cesty</th>
            <th>Místo</th>
            <th>Datum odjezdu</th>
            <th>Stav</th>
            {showProcessedCol ? <th className="travel-orders-table__processed">Zpracováno</th> : null}
            <th>Akce</th>
          </tr>
        </thead>
        <tbody>
          {processedOrders.length === 0 ? (
            <tr>
              <td colSpan={tableColSpan} className="travel-orders-list-empty">
                Žádné příkazy neodpovídají filtru.{' '}
                {listFiltersActive && (
                  <button type="button" className="btn-linklike" onClick={resetListFilters}>
                    Zrušit filtry
                  </button>
                )}
              </td>
            </tr>
          ) : (
            processedOrders.map((order) => (
              <tr
                key={order.id}
                className={order.financist_processed_at ? 'travel-orders-row--processed' : undefined}
              >
                {showPayrollPick ? (
                  <td className="travel-orders-table__check">
                    {order.status === 'closed' && !order.payroll_sent_at ? (
                      <input
                        type="checkbox"
                        checked={payrollSelectedIds.includes(order.id)}
                        onChange={(e) => onTogglePayrollSelect?.(order.id, e.target.checked)}
                        title="Zařadit do předpisu výplat pro personální oddělení"
                        aria-label={`Zařadit příkaz ${order.order_number} do předpisu`}
                      />
                    ) : (
                      <span
                        className="travel-orders-table__check-dash"
                        title={
                          order.status === 'closed' && order.payroll_sent_at
                            ? 'Již odesláno personálnímu — nelze znovu zařadit'
                            : undefined
                        }
                      >
                        {order.status === 'closed' && order.payroll_sent_at ? '✓' : '—'}
                      </span>
                    )}
                  </td>
                ) : null}
                <td>{order.order_number}</td>
                <td>{order.employee_name}</td>
                <td className="purpose-cell">{order.purpose}</td>
                <td>{order.destination_place}</td>
                <td>{new Date(order.departure_date).toLocaleDateString('cs-CZ')}</td>
                <td>{getStatusBadge(order)}</td>
                {showProcessedCol ? (
                  <td className="travel-orders-table__processed">
                    {order.status === 'completed' || order.status === 'closed' ? (
                      <input
                        type="checkbox"
                        checked={!!order.financist_processed_at}
                        onChange={() => onToggleFinancistProcessed?.(order)}
                        title={
                          order.financist_processed_at
                            ? `Zpracováno: ${new Date(order.financist_processed_at).toLocaleString('cs-CZ')}`
                            : 'Označit jako zpracováno'
                        }
                        aria-label={`Zpracováno: příkaz ${order.order_number}`}
                      />
                    ) : (
                      <span className="travel-orders-table__check-dash">—</span>
                    )}
                  </td>
                ) : null}
                <td>
                  <button
                    className="btn-view"
                    onClick={() => onViewDetails(order.id)}
                    title="Zobrazit detail"
                  >
                    👁️
                  </button>
                  {((order.status === 'draft' && viewMode === 'employee') || isItAdmin) && (
                    <button className="btn-delete" onClick={() => onDelete(order.id)} title="Smazat">
                      🗑️
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

function sliceDateForInput(iso: string | undefined): string {
  if (!iso) return ''
  return iso.length >= 10 ? iso.slice(0, 10) : iso
}

function sliceTimeForInput(iso: string | undefined): string {
  if (!iso) return ''
  const t = iso.includes('T') ? iso.split('T')[1] || '' : iso
  return t.length >= 5 ? t.slice(0, 5) : t
}

function compareTimeHHMM(a: string, b: string): number {
  const pa = a.trim().length >= 5 ? a.trim().slice(0, 5) : ''
  const pb = b.trim().length >= 5 ? b.trim().slice(0, 5) : ''
  if (!pa || !pb) return 0
  if (pa < pb) return -1
  if (pa > pb) return 1
  return 0
}

// ============================================
// ФОРМА СОЗДАНИЯ ПРИКАЗА
// ============================================

interface CreateTravelOrderFormProps {
  onSuccess: () => void
  onCancel: () => void
}

/** Počátek služební cesty — organizačně vždy pracoviště v Chebu. */
const TRAVEL_ORDER_DEPARTURE_PLACE_LABEL = 'Cheb – pracoviště školy'

const TRAVEL_TRANSPORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'Vlakové / autobusové spojení', label: 'Vlakové / autobusové spojení' },
  { value: 'MHD', label: 'MHD' },
  { value: 'Služební vozidlo školy', label: 'Služební vozidlo školy' },
  { value: 'Vlastní vozidlo', label: 'Vlastní vozidlo' },
  { value: 'Letadlo', label: 'Letadlo' },
  { value: 'Jiné', label: 'Jiné' },
]

function CreateTravelOrderForm({ onSuccess, onCancel }: CreateTravelOrderFormProps) {
  const { user } = useAuth()
  const [supervisors, setSupervisors] = useState<SupervisorOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    employee_personal_number: '',
    employee_address: '',
    employee_phone: '',
    supervisor_id: '',
    departure_date: '',
    departure_time: '',
    destination_place: '',
    destination_country: 'Česká republika',
    purpose: '',
    accommodation_required: false,
    estimated_accommodation: '',
    accommodation_description: '',
    estimated_total: '',
    expected_return_date: '',
    expected_return_time: '',
    transport_type: '',
    transport_other: '',
    vehicle_type: '',
    vehicle_registration: '',
  })

  const [companionLines, setCompanionLines] = useState<string[]>([''])

  useEffect(() => {
    loadSupervisorsData()
  }, [])

  useEffect(() => {
    let cancelled = false
    loadPersonalNumber()
      .then((pn) => {
        if (!cancelled && pn) {
          setFormData((prev) => ({ ...prev, employee_personal_number: pn }))
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [user?.id])

  const loadSupervisorsData = async () => {
    try {
      const data = await loadTravelOrderSupervisors()
      setSupervisors(data)
    } catch (err: any) {
      setError(err.message || 'Chyba při načítání schvalujících')
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const name = e.target.name
    const value = e.target.value
    setFormData((prev) => {
      let next = { ...prev, [name]: value }
      if (name === 'departure_date' && value && prev.expected_return_date && prev.expected_return_date < value) {
        next = { ...next, expected_return_date: value }
      }
      return next
    })
  }

  const isOwnVehicle = formData.transport_type === 'Vlastní vozidlo'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (formData.expected_return_date.trim() && formData.departure_date.trim()) {
      if (formData.expected_return_date < formData.departure_date) {
        setError('Předpokládaný konec cesty nemůže být dříve než datum odjezdu.')
        setLoading(false)
        return
      }
      if (
        formData.expected_return_date === formData.departure_date &&
        formData.departure_time.trim() &&
        formData.expected_return_time.trim() &&
        compareTimeHHMM(formData.expected_return_time, formData.departure_time) < 0
      ) {
        setError('Pokud je návrat ve stejný den jako odjezd, čas návratu nemůže být dříve než čas odjezdu.')
        setLoading(false)
        return
      }
    }
    if (formData.accommodation_required) {
      const acc = formData.estimated_accommodation.trim() === '' ? NaN : parseFloat(formData.estimated_accommodation)
      if (Number.isNaN(acc) || acc < 0) {
        setError('U ubytování uveďte předpokládanou cenu.')
        setLoading(false)
        return
      }
    }
    if (formData.transport_type === 'Jiné' && !formData.transport_other.trim()) {
      setError('U volby „Jiné“ doplňte konkrétní dopravní prostředek.')
      setLoading(false)
      return
    }
    if (isOwnVehicle) {
      if (!formData.vehicle_type.trim() || !formData.vehicle_registration.trim()) {
        setError('U vlastního vozidla vyplňte značku, typ a SPZ.')
        setLoading(false)
        return
      }
    }

    const transportResolved =
      formData.transport_type === 'Jiné'
        ? `Jiné: ${formData.transport_other.trim()}`
        : formData.transport_type.trim()

    if (!user?.name?.trim()) {
      setError('V účtu chybí jméno — nelze odeslat příkaz. Kontaktujte správce IT.')
      setLoading(false)
      return
    }

    try {
      const companions = companionLines.map((l) => l.trim()).filter(Boolean)

      await createTravelOrder({
        employee_personal_number: formData.employee_personal_number.trim(),
        employee_address: formData.employee_address.trim(),
        employee_phone: formData.employee_phone.trim(),
        supervisor_id: formData.supervisor_id,
        purpose: formData.purpose.trim(),
        destination_place: formData.destination_place.trim(),
        destination_country: formData.destination_country.trim() || 'Česká republika',
        departure_date: formData.departure_date,
        departure_time: formData.departure_time,
        expected_return_date: formData.expected_return_date,
        expected_return_time: formData.expected_return_time.trim() || undefined,
        transport_type: transportResolved,
        vehicle_used: isOwnVehicle,
        vehicle_type: isOwnVehicle ? formData.vehicle_type.trim() : undefined,
        vehicle_registration: isOwnVehicle ? formData.vehicle_registration.trim() : undefined,
        accommodation_required: formData.accommodation_required,
        estimated_accommodation:
          formData.accommodation_required && formData.estimated_accommodation.trim() !== ''
            ? parseFloat(formData.estimated_accommodation)
            : undefined,
        accommodation_description: formData.accommodation_description.trim() || undefined,
        companions: companions.length > 0 ? companions : undefined,
        estimated_total:
          formData.estimated_total.trim() !== ''
            ? parseFloat(formData.estimated_total.trim().replace(',', '.'))
            : undefined,
      })

      onSuccess()
    } catch (err: any) {
      setError(err.message || 'Chyba při vytváření příkazu')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form className="travel-order-form" onSubmit={handleSubmit}>
      <h2>✈️ Nový cestovní příkaz</h2>

      {error && <div className="error-message">{error}</div>}

      <div className="form-section">
        <h3>Údaje žadatele</h3>
        <div className="travel-order-departure-place" style={{ marginBottom: 16 }}>
          <strong>Příjmení a jméno</strong> se při odeslání doplní automaticky z vašeho účtu:{' '}
          <span>{user?.name?.trim() || '— (není v účtu)'}</span>
          {user?.name?.trim() && (
            <span className="field-hint" style={{ display: 'block', marginTop: 8, marginBottom: 0 }}>
              Jméno bere systém z účtu (Azure / uživatelé). Titul není samostatné pole — pokud ho potřebujete v příkazu, musí být součástí jména v účtu.
            </span>
          )}
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label>Osobní číslo *</label>
            <span className="field-hint">
              {formData.employee_personal_number
                ? 'Načteno automaticky ze seznamu zaměstnanců.'
                : 'Osobní číslo v systému chybí — kontaktujte personalistku.'}
            </span>
            <input
              type="text"
              name="employee_personal_number"
              value={formData.employee_personal_number}
              readOnly
              required
            />
          </div>
          <div className="form-group form-group-span-2">
            <label>Bydliště *</label>
            <input
              type="text"
              name="employee_address"
              value={formData.employee_address}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Telefon *</label>
            <input
              type="tel"
              name="employee_phone"
              value={formData.employee_phone}
              onChange={handleChange}
              required
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Schválení</h3>
        <div className="form-group">
          <label>Vyberte svého přímého nadřízeného *</label>
          <span className="field-hint">
            Povinné — stejně jako v žádosti o dovolenou vyberte svého přímého nadřízeného.
          </span>
          <select name="supervisor_id" value={formData.supervisor_id} onChange={handleChange} required>
            <option value="">Vyberte svého přímého nadřízeného…</option>
            {supervisors.map((supervisor) => (
              <option key={supervisor.user_id} value={supervisor.user_id}>
                {supervisor.full_name} ({supervisor.role})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-section">
        <h3>Cesta</h3>
        <div className="travel-order-departure-place">
          <strong>Počátek cesty (místo odjezdu):</strong> {TRAVEL_ORDER_DEPARTURE_PLACE_LABEL}
          <span className="field-hint" style={{ marginTop: 8, marginBottom: 0 }}>
            Služební cesta začíná vždy na pracovišti školy v Chebu; vyplňte jen datum a hodinu odjezdu.
          </span>
        </div>
        <div className="form-grid">
          <div className="form-group">
            <label>Počátek cesty — datum *</label>
            <input type="date" name="departure_date" value={formData.departure_date} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Počátek cesty — hodina *</label>
            <input type="time" name="departure_time" value={formData.departure_time} onChange={handleChange} required />
          </div>
          <div className="form-group form-group-span-2">
            <label>Místo jednání *</label>
            <input
              type="text"
              name="destination_place"
              value={formData.destination_place}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Stát</label>
            <input type="text" name="destination_country" value={formData.destination_country} onChange={handleChange} />
          </div>
          <div className="form-group form-group-span-2">
            <label>Účel cesty *</label>
            <textarea name="purpose" value={formData.purpose} onChange={handleChange} rows={3} required />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Ubytování</h3>
        <span className="field-hint" style={{ display: 'block', marginBottom: 12 }}>
          V případě kladné odpovědi uveďte předpokládanou cenu.
        </span>
        <div className="form-group travel-order-radio-row">
          <span className="travel-order-radio-label">Ubytování</span>
          <label className="travel-order-radio">
            <input
              type="radio"
              name="accommodation_required"
              checked={!formData.accommodation_required}
              onChange={() => setFormData({ ...formData, accommodation_required: false })}
            />
            Ne
          </label>
          <label className="travel-order-radio">
            <input
              type="radio"
              name="accommodation_required"
              checked={formData.accommodation_required}
              onChange={() => setFormData({ ...formData, accommodation_required: true })}
            />
            Ano — uveďte předpokládanou cenu
          </label>
        </div>
        {formData.accommodation_required && (
          <div className="form-grid">
            <div className="form-group">
              <label>Předpokládaná cena ubytování (Kč) *</label>
              <input
                type="number"
                name="estimated_accommodation"
                value={formData.estimated_accommodation}
                onChange={handleChange}
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="form-group form-group-span-2">
              <label>Popis ubytování</label>
              <input
                type="text"
                name="accommodation_description"
                value={formData.accommodation_description}
                onChange={handleChange}
              />
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Odhad výdajů</h3>
        <div className="form-grid">
          <div className="form-group form-group-span-2">
            <label>Předpokládaná částka výdajů (Kč)</label>
            <input
              type="text"
              name="estimated_total"
              value={formData.estimated_total}
              onChange={handleChange}
              inputMode="decimal"
              placeholder="např. odhad celkových nákladů (nepovinné)"
            />
            <span className="field-hint" style={{ display: 'block', marginTop: 6 }}>
              Volitelný odhad pro vedoucího — při schválení příkazu může částku potvrdit nebo upravit.
            </span>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Konec cesty</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Předpokládaný konec cesty — datum *</label>
            <span className="field-hint">
              Pokud je návrat ve stejný den jako odjezd, čas návratu nesmí být dříve než čas odjezdu.
            </span>
            <input
              type="date"
              name="expected_return_date"
              value={formData.expected_return_date}
              onChange={handleChange}
              min={formData.departure_date || undefined}
              required
            />
          </div>
          <div className="form-group">
            <label>Čas návratu (nepovinné)</label>
            <input
              type="time"
              name="expected_return_time"
              value={formData.expected_return_time}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Spolucestující (položka 3 ve vzoru příkazu)</h3>
        <span className="field-hint" style={{ display: 'block', marginBottom: 12 }}>
          Každou osobu zadejte zvlášť. Prázdné řádky se při odeslání ignorují.
        </span>
        {companionLines.map((line, idx) => (
          <div key={idx} className="form-group travel-order-companion-row" style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label>Spolucestující {idx + 1}</label>
              <input
                type="text"
                value={line}
                onChange={(e) => {
                  const next = [...companionLines]
                  next[idx] = e.target.value
                  setCompanionLines(next)
                }}
                placeholder="Jméno a příjmení"
              />
            </div>
            {companionLines.length > 1 && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setCompanionLines(companionLines.filter((_, i) => i !== idx))}
              >
                Odstranit
              </button>
            )}
          </div>
        ))}
        <button type="button" className="btn-secondary" onClick={() => setCompanionLines([...companionLines, ''])}>
          + Přidat spolucestujícího
        </button>
      </div>

      <div className="form-section">
        <h3>Doprava</h3>
        <div className="form-group">
          <label>Požadovaný dopravní prostředek *</label>
          <span className="field-hint">Vyberte z nabídky. Při „Vlastní vozidlo“ doplňte údaje vozidla.</span>
          <select name="transport_type" value={formData.transport_type} onChange={handleChange} required>
            <option value="">Vyberte…</option>
            {TRAVEL_TRANSPORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {formData.transport_type === 'Jiné' && (
          <div className="form-group">
            <label>Konkrétní dopravní prostředek *</label>
            <input
              type="text"
              name="transport_other"
              value={formData.transport_other}
              onChange={handleChange}
              placeholder="např. sdílené auto, taxi…"
              required
            />
          </div>
        )}
        {isOwnVehicle && (
          <div className="form-grid">
            <div className="form-group form-group-span-2">
              <span className="field-hint" style={{ marginBottom: 8 }}>
                Povinné u vlastního vozidla; údaje se propíší do vyúčtování.
              </span>
            </div>
            <div className="form-group">
              <label>Značka, typ *</label>
              <input type="text" name="vehicle_type" value={formData.vehicle_type} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>SPZ *</label>
              <input type="text" name="vehicle_registration" value={formData.vehicle_registration} onChange={handleChange} required />
            </div>
          </div>
        )}
      </div>

      <p className="travel-order-form-legend">* povinný údaj.</p>

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
          Zrušit
        </button>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Odesílání…' : 'ODESLAT KE SCHVÁLENÍ'}
        </button>
      </div>
    </form>
  )
}

// ============================================
// ДЕТАЛИ ПРИКАЗА
// ============================================

interface TravelOrderDetailsProps {
  order: TravelOrder
  viewMode: ViewMode
  /** Uzavření dokončeného CP v režimu ekonom — ředitel / ekonom / IT (API stejně). */
  canCloseCompleted: boolean
  onClose: () => void
  onApprove: (opts?: {
    estimated_total?: number | null
    advance_payment?: number | null
    advance_payment_date?: string | null
  }) => void
  onReject: () => void
  onComplete: () => void
  onFinalizeSettlement: () => void
  onDownloadExcelA: () => void
  onDownloadExcelB: () => void
  onReportReturn: () => void
  onConfirmReturn: () => void
  onApproveSettlement: () => void
  onRejectSettlement: () => void
  onCloseFinancist: () => void
  onToggleFinancistProcessed?: () => void
  onEditDocumentB: () => void
}

function TravelOrderDetails({
  order,
  viewMode,
  canCloseCompleted,
  onClose,
  onApprove,
  onReject,
  onComplete,
  onFinalizeSettlement,
  onDownloadExcelA,
  onDownloadExcelB,
  onReportReturn,
  onConfirmReturn,
  onApproveSettlement,
  onRejectSettlement,
  onCloseFinancist,
  onToggleFinancistProcessed,
  onEditDocumentB,
}: TravelOrderDetailsProps) {
  const { user: detailUser } = useAuth()
  const orderStatus = order.status
  const isFinancistPanelUser = hasFinancistAccess(detailUser)
  /** Dokument B: ekonom (a správce IT v režimu ekonom) kdykoli; vedoucí i ve stavu čeká na schválení; zaměstnanec po potvrzení ekonoma. */
  const canDownloadDocumentB =
    (viewMode === 'financist' && isFinancistPanelUser) ||
    (viewMode === 'supervisor' &&
      detailUser?.id &&
      order.supervisor_id === detailUser.id &&
      (orderStatus === 'pending_settlement_supervisor' ||
        orderStatus === 'completed' ||
        orderStatus === 'closed')) ||
    (viewMode === 'employee' &&
      detailUser?.id &&
      order.employee_id === detailUser.id &&
      (orderStatus === 'completed' || orderStatus === 'closed'))

  const [approveEstimatedTotal, setApproveEstimatedTotal] = useState(() =>
    order.estimated_total != null && String(order.estimated_total).trim() !== ''
      ? String(order.estimated_total)
      : '',
  )
  const [approveAdvancePayment, setApproveAdvancePayment] = useState(() =>
    order.advance_payment != null && String(order.advance_payment).trim() !== ''
      ? String(order.advance_payment)
      : '',
  )
  const [approveAdvanceDate, setApproveAdvanceDate] = useState(
    () => sliceDateForInput(order.advance_payment_date) || '',
  )
  useEffect(() => {
    setApproveEstimatedTotal(
      order.estimated_total != null && String(order.estimated_total).trim() !== ''
        ? String(order.estimated_total)
        : '',
    )
    setApproveAdvancePayment(
      order.advance_payment != null && String(order.advance_payment).trim() !== ''
        ? String(order.advance_payment)
        : '',
    )
    setApproveAdvanceDate(sliceDateForInput(order.advance_payment_date) || '')
  }, [order.id, order.estimated_total, order.advance_payment, order.advance_payment_date])

  return (
    <div className="order-details">
      <div className="details-header">
        <h2>Cestovní příkaz #{order.order_number}</h2>
        <button className="btn-close" onClick={onClose}>✕</button>
      </div>

      <div className="details-content">
        <div className="detail-section">
          <h3>Pracovník</h3>
          {order.employee_personal_number && <p><strong>Osobní číslo:</strong> {order.employee_personal_number}</p>}
          <p><strong>Jméno:</strong> {order.employee_name}</p>
          {order.employee_title && <p><strong>Titul:</strong> {order.employee_title}</p>}
          {order.employee_position && <p><strong>Funkce:</strong> {order.employee_position}</p>}
          {order.employee_department && <p><strong>Oddělení:</strong> {order.employee_department}</p>}
          {order.employee_address && <p><strong>Bydliště:</strong> {order.employee_address}</p>}
          {order.employee_phone && <p><strong>Telefon:</strong> {order.employee_phone}</p>}
        </div>

        <div className="detail-section">
          <h3>Cesta</h3>
          <p><strong>Účel:</strong> {order.purpose}</p>
          <p><strong>Místo jednání:</strong> {order.destination_place}, {order.destination_country}</p>
          <p>
            <strong>Místo odjezdu (počátek cesty):</strong> {TRAVEL_ORDER_DEPARTURE_PLACE_LABEL}
          </p>
          <p>
            <strong>Datum a čas odjezdu:</strong> {new Date(order.departure_date).toLocaleDateString('cs-CZ')}{' '}
            {order.departure_time || ''}
          </p>
          {order.expected_return_date && (
            <p>
              <strong>Předpokládaný konec:</strong>{' '}
              {new Date(order.expected_return_date).toLocaleDateString('cs-CZ')} {order.expected_return_time || ''}
            </p>
          )}
          {order.companions && order.companions.length > 0 ? (
            <div>
              <strong>Spolucestující:</strong>
              <ul style={{ margin: '4px 0 0 18px' }}>
                {order.companions.map((c) => (
                  <li key={c.id}>{c.full_name}</li>
                ))}
              </ul>
            </div>
          ) : (
            order.travel_companions && <p><strong>Spolucestující:</strong> {order.travel_companions}</p>
          )}
          {order.transport_type && <p><strong>Dopravní prostředek:</strong> {order.transport_type}</p>}
          {(order.vehicle_used === true ||
            order.vehicle_used === 1 ||
            String(order.vehicle_used) === '1') && (
            <p>
              <strong>Vlastní vozidlo:</strong> {order.vehicle_type || '—'}, SPZ {order.vehicle_registration || '—'}
            </p>
          )}
          {(order.accommodation_required === 1 ||
            order.accommodation_required === true ||
            String(order.accommodation_required) === '1') && (
            <p>
              <strong>Ubytování:</strong> ano
              {order.estimated_accommodation != null && ` — předpoklad ${order.estimated_accommodation} Kč`}
              {order.accommodation_description && ` — ${order.accommodation_description}`}
            </p>
          )}
        </div>

        <div className="detail-section">
          <h3>Náklady</h3>
          <p>
            <strong>Předpokládaná částka výdajů:</strong>{' '}
            {order.estimated_total != null && String(order.estimated_total).trim() !== ''
              ? `${order.estimated_total} Kč`
              : '—'}
          </p>
          {order.advance_payment != null && (
            <p><strong>Povolená záloha:</strong> {order.advance_payment} Kč</p>
          )}
          {order.actual_total != null && <p><strong>Skutečnost celkem:</strong> {order.actual_total} Kč</p>}
          {orderStatus === 'completed' ||
          orderStatus === 'closed' ||
          orderStatus === 'pending_settlement_supervisor' ? (
            <>
              {order.actual_transport_cost != null && (
                <p>
                  <strong>Jízdné (součet z tabulky):</strong> {order.actual_transport_cost} Kč
                </p>
              )}
              {order.actual_daily_allowance != null && (
                <p>
                  <strong>Stravné:</strong> {order.actual_daily_allowance} Kč
                </p>
              )}
              {order.actual_accommodation != null && (
                <p>
                  <strong>Ubytování:</strong> {order.actual_accommodation} Kč
                </p>
              )}
              {order.actual_other_costs != null && (
                <p>
                  <strong>Vedlejší výdaje:</strong> {order.actual_other_costs} Kč
                </p>
              )}
              {order.actual_fuel_costs != null && Number(order.actual_fuel_costs) > 0 && (
                <p>
                  <strong>PHM:</strong> {order.actual_fuel_costs} Kč
                  {order.vehicle_fuel_type && ` (${order.vehicle_fuel_type})`}
                </p>
              )}
              {/* amortizace se neúčtuje */}
              {order.advance_payment != null && order.actual_total != null && (
                <p>
                  <strong>Vyúčtování zálohy:</strong>{' '}
                  {(Number(order.actual_total) - Number(order.advance_payment)).toFixed(2)} Kč (celkem − záloha)
                </p>
              )}
            </>
          ) : null}
        </div>

        {order.report_text && String(order.report_text).trim() !== '' && (
          <div className="detail-section">
            <h3>Text vyúčtování / průběh cesty</h3>
            <p className="travel-order-notes-readonly" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {String(order.report_text).trim()}
            </p>
          </div>
        )}

        {(order.settlement_attachments || []).length > 0 ? (
          <div className="detail-section">
            <h3>Přiložené doklady ({(order.settlement_attachments || []).length})</h3>
            <p className="field-hint">Soubory nahrané zaměstnancem při vyúčtování (účtenky, doklady…).</p>
            <ul className="travel-order-settlement-attachments-readonly">
              {(order.settlement_attachments || []).map((att) => (
                <li key={att.id || att.file_url}>
                  <a href={protectedFileHref(att.file_url)} target="_blank" rel="noopener noreferrer">
                    {att.original_filename || 'Soubor'}
                  </a>
                  {att.record_date ? (
                    <span className="travel-order-attachment-desc">
                      {' '}
                      ({sliceDateForInput(String(att.record_date))})
                    </span>
                  ) : null}
                  {att.description ? (
                    <span className="travel-order-attachment-desc"> — {att.description}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="detail-section">
            <h3>Přiložené doklady</h3>
            <p className="field-hint">Žádné přílohy k vyúčtování.</p>
          </div>
        )}

        {order.daily_records && order.daily_records.length > 0 && (
          <div className="detail-section">
            <h3>Vyúčtování — přehled po dnech</h3>
            <p className="field-hint">
              Přehled je optimalizovaný pro čtení. Pokud potřebujete původní tabulku, rozbalte ji níže.
            </p>

            {order.vehicle_used && (Number(order.vehicle_total_cost || 0) > 0 || Number(order.actual_fuel_costs || 0) > 0) && (
              <div className="travel-order-day-card" style={{ marginBottom: 14 }}>
                <div className="travel-order-day-card__title">Soukromé vozidlo — náklady</div>
                <div className="form-grid" style={{ marginTop: 10 }}>
                  <div className="form-group">
                    <label>Náhrada za km</label>
                    <div>{Number(order.vehicle_total_cost || 0).toFixed(2)} Kč</div>
                  </div>
                  <div className="form-group">
                    <label>PHM</label>
                    <div>{Number(order.actual_fuel_costs || 0).toFixed(2)} Kč</div>
                  </div>
                  <div className="form-group">
                    <label>Celkem</label>
                    <div style={{ fontWeight: 700 }}>
                      {(Number(order.vehicle_total_cost || 0) + Number(order.actual_fuel_costs || 0)).toFixed(2)} Kč
                    </div>
                  </div>
                  <div className="form-group form-group--wide">
                    <label>Vozidlo / SPZ</label>
                    <div>
                      {order.vehicle_type || '—'}
                      {order.vehicle_registration ? ` (${order.vehicle_registration})` : ''}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {order.daily_records.map((dr) => {
              const dateLabel = sliceDateForInput(dr.record_date)
              const total =
                dr.row_total != null
                  ? Number(dr.row_total)
                  : Number(dr.fare_and_local_transport || 0) +
                      Number(dr.leg2_fare || 0) +
                      Number(dr.stravne_kc || 0) +
                      Number(dr.overnight_allowance || 0) +
                      Number(dr.incidental_expenses || 0)

              const leg2 =
                [dr.leg2_departure_date, dr.leg2_departure_time, dr.leg2_arrival_date, dr.leg2_arrival_time].some(Boolean)
                  ? `${dr.leg2_departure_date ? sliceDateForInput(dr.leg2_departure_date) : ''} ${dr.leg2_departure_time || ''} → ${dr.leg2_arrival_date ? sliceDateForInput(dr.leg2_arrival_date) : ''} ${dr.leg2_arrival_time || ''}`.trim()
                  : ''

              const dayAttachments = (order.settlement_attachments || []).filter((att) => {
                const ad = sliceDateForInput((att.record_date as string) || '')
                return ad !== '' && ad === dateLabel
              })

              return (
                <div key={dr.id} className="travel-order-day-card">
                  <div className="travel-order-day-card__title">
                    {dateLabel} — {total.toFixed(2)} Kč
                  </div>
                  <div className="form-grid" style={{ marginTop: 10 }}>
                    <div className="form-group form-group--wide">
                      <label>Použitý dopravní prostředek</label>
                      <div>{dr.transport_means || '—'}</div>
                    </div>
                    <div className="form-group form-group--wide">
                      <label>Odjezd (text)</label>
                      <div>{dr.day_route || '—'}</div>
                    </div>
                    <div className="form-group">
                      <label>Odjezd — datum / čas</label>
                      <div>
                        {dateLabel} {dr.departure_time || ''}
                      </div>
                    </div>
                    <div className="form-group form-group--wide">
                      <label>Místo jednání</label>
                      <div>
                        {formatMeetingPlaceWithReturn(dr.meeting_place, meetingPlaceReturnFromNotes(dr.notes))}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Příjezd — datum / čas</label>
                      <div>
                        {dr.arrival_date ? sliceDateForInput(dr.arrival_date) : '—'} {dr.arrival_time || ''}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Jízdné a místní přeprava</label>
                      <div>{Number(dr.fare_and_local_transport || 0).toFixed(2)} Kč</div>
                    </div>
                    <div className="form-group">
                      <label>Vedlejší výdaje</label>
                      <div>{Number(dr.incidental_expenses || 0).toFixed(2)} Kč</div>
                    </div>
                    <div className="form-group form-group--wide">
                      <label>2. úsek (cesta zpět / druhý úsek)</label>
                      <div>{leg2 || '—'}</div>
                    </div>
                    <div className="form-group">
                      <label>Jízdné 2. úsek</label>
                      <div>{Number(dr.leg2_fare || 0).toFixed(2)} Kč</div>
                    </div>
                    <div className="form-group">
                      <label>Ubytování, nocležné</label>
                      <div>{Number(dr.overnight_allowance || 0).toFixed(2)} Kč</div>
                    </div>
                    <div className="form-group">
                      <label>Stravné</label>
                      <div>{Number(dr.stravne_kc || 0).toFixed(2)} Kč</div>
                    </div>
                    {dr.notes && String(dr.notes).trim() ? (
                      <div className="form-group form-group--wide">
                        <label>Texty k výdajům / poznámka</label>
                        <div className="travel-order-notes-readonly">{String(dr.notes)}</div>
                      </div>
                    ) : null}
                  </div>
                  {dayAttachments.length > 0 && (
                    <div className="travel-order-day-card__doklady-readonly">
                      <div className="form-group form-group--wide">
                        <label>Doklady k tomuto dni</label>
                        <ul className="travel-order-settlement-attachments-readonly">
                          {dayAttachments.map((att) => (
                            <li key={att.id}>
                              <a href={protectedFileHref(att.file_url)} target="_blank" rel="noopener noreferrer">
                                {att.original_filename || 'Soubor'}
                              </a>
                              {att.description ? (
                                <span className="travel-order-attachment-desc"> — {att.description}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}

            {(() => {
              const all = order.settlement_attachments || []
              if (all.length === 0) return null
              const dailyDates = new Set(
                (order.daily_records || []).map((d) => sliceDateForInput(d.record_date)).filter(Boolean),
              )
              const orphan = all.filter((att) => {
                const ad = sliceDateForInput((att.record_date as string) || '')
                if (!ad) return true
                return !dailyDates.has(ad)
              })
              if (orphan.length === 0) return null
              return (
                <div className="travel-order-day-card" style={{ marginTop: 14 }}>
                  <div className="travel-order-day-card__title">Další doklady</div>
                  <p className="field-hint">Přílohy bez přiřazeného dne v tabulce (starší zápis nebo nesoulad dat).</p>
                  <ul className="travel-order-settlement-attachments-readonly">
                    {orphan.map((att) => (
                      <li key={att.id}>
                        <a href={protectedFileHref(att.file_url)} target="_blank" rel="noopener noreferrer">
                          {att.original_filename || 'Soubor'}
                        </a>
                        {att.description ? <span className="travel-order-attachment-desc"> — {att.description}</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })()}

            <details className="travel-order-optional-block" style={{ marginTop: 10 }}>
              <summary>Zobrazit původní tabulku (technický detail)</summary>
              <div className="travel-order-daily-table-wrap">
                <table className="travel-order-daily-table">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Odj.</th>
                      <th>Příj. datum</th>
                      <th>Příj. čas</th>
                      <th>Odjezd (text)</th>
                      <th>Místo jedn.</th>
                      <th>Doprava</th>
                      <th>Ztráta h</th>
                      <th>km</th>
                      <th>Práce</th>
                      <th>Jízdné</th>
                      <th>Jízdné 2</th>
                      <th>Stravné</th>
                      <th>Nocležné</th>
                      <th>Vedl.</th>
                      <th>Náhrada za vozidlo</th>
                      <th>PHM</th>
                      <th>2. úsek (od–do)</th>
                      <th>Celkem</th>
                      <th>Pozn.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.daily_records.map((dr, rowIdx) => {
                      const showVeh =
                        rowIdx === 0 &&
                        order.vehicle_used &&
                        (Number(order.vehicle_total_cost || 0) > 0 || Number(order.actual_fuel_costs || 0) > 0)
                      const vehKm = Number(order.vehicle_total_cost || 0)
                      const vehFuel = Number(order.actual_fuel_costs || 0)
                      const daySumWithoutVehicle =
                        dr.row_total != null
                          ? Number(dr.row_total)
                          : Number(dr.fare_and_local_transport || 0) +
                            Number(dr.leg2_fare || 0) +
                            Number(dr.stravne_kc || 0) +
                            Number(dr.overnight_allowance || 0) +
                            Number(dr.incidental_expenses || 0)
                      const celkemDay =
                        showVeh && Number.isFinite(daySumWithoutVehicle)
                          ? daySumWithoutVehicle + vehKm + vehFuel
                          : daySumWithoutVehicle
                      const vehNote =
                        showVeh && (order.vehicle_registration || order.vehicle_fuel_type || order.vehicle_type)
                          ? [
                              order.vehicle_type ? String(order.vehicle_type) : '',
                              order.vehicle_registration ? `SPZ ${order.vehicle_registration}` : '',
                              order.vehicle_fuel_type ? `PHM ${order.vehicle_fuel_type}` : '',
                            ]
                              .filter(Boolean)
                              .join('; ')
                          : ''
                      const pozn =
                        dr.notes && vehNote ? `${dr.notes}\n${vehNote}` : dr.notes || vehNote || '—'
                      return (
                        <tr key={dr.id}>
                          <td>{sliceDateForInput(dr.record_date)}</td>
                          <td>{dr.departure_time || '—'}</td>
                          <td>{dr.arrival_date ? sliceDateForInput(dr.arrival_date) : '—'}</td>
                          <td>{dr.arrival_time || '—'}</td>
                          <td>{dr.day_route || '—'}</td>
                          <td>{formatMeetingPlaceWithReturn(dr.meeting_place, meetingPlaceReturnFromNotes(dr.notes))}</td>
                          <td>{dr.transport_means || '—'}</td>
                          <td>{dr.hours_time_lost ?? '—'}</td>
                          <td>{dr.distance_km ?? '—'}</td>
                          <td>{[dr.work_performance_from, dr.work_performance_to].filter(Boolean).join(' – ') || '—'}</td>
                          <td>{dr.fare_and_local_transport ?? '—'}</td>
                          <td>{dr.leg2_fare ?? '—'}</td>
                          <td>{dr.stravne_kc ?? '—'}</td>
                          <td>{dr.overnight_allowance ?? '—'}</td>
                          <td>{dr.incidental_expenses ?? '—'}</td>
                          <td>{showVeh ? vehKm.toFixed(2) : '—'}</td>
                          <td>{showVeh ? vehFuel.toFixed(2) : '—'}</td>
                          <td>
                            {[dr.leg2_departure_date, dr.leg2_departure_time, dr.leg2_arrival_date, dr.leg2_arrival_time].some(Boolean)
                              ? `${dr.leg2_departure_date ? sliceDateForInput(dr.leg2_departure_date) : ''} ${dr.leg2_departure_time || ''} → ${dr.leg2_arrival_date ? sliceDateForInput(dr.leg2_arrival_date) : ''} ${dr.leg2_arrival_time || ''}`.trim()
                              : '—'}
                          </td>
                          <td>{celkemDay ? celkemDay.toFixed(2) : '—'}</td>
                          <td className="travel-order-daily-table__pozn" style={{ whiteSpace: 'pre-wrap' }}>
                            {pozn}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const rows = order.daily_records || []
                      const sumFare = rows.reduce((a, r) => a + Number(r.fare_and_local_transport || 0), 0)
                      const sumFare2 = rows.reduce((a, r) => a + Number(r.leg2_fare || 0), 0)
                      const sumStr = rows.reduce((a, r) => a + Number(r.stravne_kc || 0), 0)
                      const sumNoc = rows.reduce((a, r) => a + Number(r.overnight_allowance || 0), 0)
                      const sumInc = rows.reduce((a, r) => a + Number(r.incidental_expenses || 0), 0)
                      const sumTotDays = rows.reduce(
                        (a, r) =>
                          a +
                          Number(
                            r.row_total != null
                              ? r.row_total
                              : Number(r.fare_and_local_transport || 0) +
                                  Number(r.leg2_fare || 0) +
                                  Number(r.stravne_kc || 0) +
                                  Number(r.overnight_allowance || 0) +
                                  Number(r.incidental_expenses || 0),
                          ),
                        0,
                      )
                      const sumNahradaVoz =
                        order.vehicle_used && (Number(order.vehicle_total_cost || 0) > 0 || Number(order.actual_fuel_costs || 0) > 0)
                          ? Number(order.vehicle_total_cost || 0)
                          : 0
                      const sumFuel = order.vehicle_used ? Number(order.actual_fuel_costs || 0) : 0
                      const sumVehicle = sumNahradaVoz + sumFuel
                      const sumTot = sumTotDays + sumVehicle
                      return (
                        <tr>
                          <td colSpan={10} style={{ fontWeight: 700 }}>
                            Celkem za cestu
                          </td>
                          <td>{sumFare.toFixed(2)}</td>
                          <td>{sumFare2.toFixed(2)}</td>
                          <td>{sumStr.toFixed(2)}</td>
                          <td>{sumNoc.toFixed(2)}</td>
                          <td>{sumInc.toFixed(2)}</td>
                          <td>{sumNahradaVoz > 0 ? sumNahradaVoz.toFixed(2) : '—'}</td>
                          <td>{sumFuel > 0 ? sumFuel.toFixed(2) : '—'}</td>
                          <td>—</td>
                          <td style={{ fontWeight: 700 }}>{sumTot.toFixed(2)}</td>
                          <td />
                        </tr>
                      )
                    })()}
                  </tfoot>
                </table>
              </div>
            </details>
          </div>
        )}

        {order.rejection_reason && (
          <div className="detail-section rejection-section">
            <h3>Důvod zamítnutí</h3>
            <p>{order.rejection_reason}</p>
          </div>
        )}

        {order.settlement_return_note &&
          String(order.settlement_return_note).trim() !== '' &&
          order.status === 'pending_completion' && (
            <div className="detail-section">
              <h3>Vyúčtování vráceno vedoucím</h3>
              <p className="travel-order-notes-readonly" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {String(order.settlement_return_note).trim()}
              </p>
            </div>
          )}

        {(order.return_reported_at || order.return_confirmed_at) && (
          <div className="detail-section">
            <h3>Návrat z cesty</h3>
            {order.return_reported_at && (
              <p><strong>Návrat nahlášen:</strong> {new Date(order.return_reported_at).toLocaleString('cs-CZ')}</p>
            )}
            {order.return_confirmed_at && (
              <p><strong>Potvrzeno vedoucím:</strong> {new Date(order.return_confirmed_at).toLocaleString('cs-CZ')}</p>
            )}
          </div>
        )}
      </div>

      <div className="details-actions">
        {viewMode === 'supervisor' && order.status === 'pending_approval' && (
          <>
            <div className="travel-order-supervisor-approve-block">
              <label htmlFor="travel-order-approve-estimated">Předpokládaná částka výdajů (Kč)</label>
              <input
                id="travel-order-approve-estimated"
                type="text"
                inputMode="decimal"
                value={approveEstimatedTotal}
                onChange={(e) => setApproveEstimatedTotal(e.target.value)}
                placeholder="např. odhad od žadatele"
              />
              <p className="field-hint">
                Při schválení můžete částku potvrdit nebo upravit — uloží se do příkazu (mj. dokument A). Prázdné pole
                smaže uložený odhad.
              </p>
              <label htmlFor="travel-order-approve-advance" style={{ marginTop: 14 }}>
                Schválená záloha (Kč)
              </label>
              <input
                id="travel-order-approve-advance"
                type="text"
                inputMode="decimal"
                value={approveAdvancePayment}
                onChange={(e) => setApproveAdvancePayment(e.target.value)}
                placeholder="0 = bez schválené zálohy"
              />
              <p className="field-hint">
                Vyplněná částka se uloží do příkazu a promítne se do dokumentu A (položka zálohy / buňka E15 dle šablony).
                Žadatel obdrží v e-mailu upozornění, že si zálohu může vyzvednout v pokladně.
              </p>
              <label htmlFor="travel-order-approve-advance-date" style={{ marginTop: 10 }}>
                Datum výplaty zálohy (nepovinné)
              </label>
              <input
                id="travel-order-approve-advance-date"
                type="date"
                value={approveAdvanceDate}
                onChange={(e) => setApproveAdvanceDate(e.target.value)}
              />
              <p className="field-hint">Pokud nevyplníte a záloha je &gt; 0, použije se datum schválení.</p>
            </div>
            <button
              type="button"
              className="btn-approve"
              onClick={() => {
                const t = approveEstimatedTotal.trim().replace(',', '.')
                let estimated_total: number | null = null
                if (t !== '') {
                  const n = parseFloat(t)
                  if (!Number.isFinite(n) || n < 0) {
                    window.alert('Zadejte platnou nezápornou částku výdajů v Kč, nebo pole nechte prázdné.')
                    return
                  }
                  estimated_total = n
                }

                const advRaw = approveAdvancePayment.trim().replace(',', '.')
                let advance_payment: number | null = null
                let advance_payment_date: string | null = null
                if (advRaw !== '') {
                  const a = parseFloat(advRaw)
                  if (!Number.isFinite(a) || a < 0) {
                    window.alert('Záloha: zadejte platné nezáporné číslo v Kč, nebo pole nechte prázdné (bez zálohy).')
                    return
                  }
                  if (a === 0) {
                    advance_payment = null
                  } else {
                    advance_payment = a
                    advance_payment_date = approveAdvanceDate.trim() || null
                  }
                }

                onApprove({ estimated_total, advance_payment, advance_payment_date })
              }}
            >
              ✓ Schválit
            </button>
            <button type="button" className="btn-reject" onClick={onReject}>✕ Zamítnout</button>
          </>
        )}
        {viewMode === 'supervisor' && order.status === 'pending_return_confirmation' && (
          <button type="button" className="btn-approve" onClick={onConfirmReturn}>✓ Potvrdit návrat zaměstnance</button>
        )}

        {viewMode === 'supervisor' && order.status === 'pending_settlement_supervisor' && (
          <>
            <p className="details-hint" style={{ maxWidth: 640 }}>
              Zaměstnanec odeslal vyúčtování. Můžete ho <strong>potvrdit</strong> (předá se hlavnímu ekonomovi),{' '}
              <strong>upravit v formuláři</strong> a pak potvrdit odeslání ekonomovi, nebo <strong>vrátit</strong> k úpravě
              zaměstnanci.
            </p>
            <button type="button" className="btn-reject" onClick={onRejectSettlement}>
              ↩ Vrátit zaměstnanci k opravě
            </button>
            <button type="button" className="btn-secondary" onClick={onComplete}>
              ✏️ Upravit a potvrdit (formulář)
            </button>
            <button type="button" className="btn-approve" onClick={onApproveSettlement}>
              ✓ Potvrdit bez úprav (ekonom)
            </button>
          </>
        )}

        {[
          'approved',
          'pending_return_confirmation',
          'pending_completion',
          'pending_settlement_supervisor',
          'completed',
          'closed',
        ].includes(order.status) && (
          <button type="button" className="btn-secondary" onClick={onDownloadExcelA}>📊 Stáhnout dokument A</button>
        )}
        {canDownloadDocumentB && (
          <button type="button" className="btn-secondary" onClick={onDownloadExcelB}>
            📄 Stáhnout dokument B
          </button>
        )}
        {viewMode === 'financist' && ['completed', 'closed'].includes(order.status) && (
          <button type="button" className="btn-secondary" onClick={onComplete}>
            ✏️ Upravit vyúčtování (formulář)
          </button>
        )}
        {viewMode === 'financist' && ['completed', 'closed'].includes(order.status) && (
          <button type="button" className="btn-primary" onClick={onEditDocumentB}>✏️ Upravit data Dokumentu B</button>
        )}

        {viewMode === 'employee' && order.status === 'approved' && (
          <button type="button" className="btn-primary" onClick={onReportReturn}>🏠 Nahlásit návrat z cesty</button>
        )}

        {viewMode === 'employee' && order.status === 'pending_completion' && (
          <>
            {(order.daily_records?.length ?? 0) > 0 ? (
              <>
                <button type="button" className="btn-secondary" onClick={onComplete}>
                  ✏️ Upravit vyúčtování
                </button>
                <button type="button" className="btn-primary" onClick={onFinalizeSettlement}>
                  📤 Odeslat vedoucímu
                </button>
              </>
            ) : (
              <button type="button" className="btn-primary" onClick={onComplete}>
                📝 Vyúčtování
              </button>
            )}
          </>
        )}

        {viewMode === 'employee' && order.status === 'pending_settlement_supervisor' && (
          <p className="details-hint">
            Vyúčtování bylo odesláno a čeká na schválení vedoucím. Po schválení bude předáno hlavnímu ekonomovi.
          </p>
        )}

        {viewMode === 'employee' && order.status === 'pending_return_confirmation' && (
          <p className="details-hint">
            U tohoto příkazu ještě čeká na potvrzení návratu vedoucím (starší stav). Nové příkazy po nahlášení návratu přejdou rovnou k vyúčtování.
          </p>
        )}

        {viewMode === 'financist' && ['completed', 'closed'].includes(order.status) && onToggleFinancistProcessed && (
          <label
            className="travel-orders-processed-toggle"
            title={
              order.financist_processed_at
                ? `Zpracováno: ${new Date(order.financist_processed_at).toLocaleString('cs-CZ')}`
                : 'Označit jako zpracováno'
            }
          >
            <input
              type="checkbox"
              checked={!!order.financist_processed_at}
              onChange={() => onToggleFinancistProcessed()}
            />
            <span>Zpracováno ekonomem</span>
          </label>
        )}

        {viewMode === 'financist' && order.status === 'completed' && canCloseCompleted && (
          <button type="button" className="btn-approve" onClick={onCloseFinancist}>✓ Uzavřít příkaz (ekonom)</button>
        )}
      </div>
    </div>
  )
}

// ============================================
// РЕДАКТОВАНИЕ DAT PRO DOKUMENT B (ekonom)
// ============================================

interface EditDocumentBFormProps {
  order: TravelOrder
  onSuccess: () => void
  onCancel: () => void
}

function EditDocumentBForm({ order, onSuccess, onCancel }: EditDocumentBFormProps) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    employee_name: order.employee_name || '',
    employee_title: order.employee_title || '',
    employee_address: order.employee_address || '',
    employee_personal_number: order.employee_personal_number || '',
    employee_department: order.employee_department || '',
    employee_phone: order.employee_phone || '',
    departure_date: sliceDateForInput(order.departure_date) || '',
    departure_time: sliceTimeForInput(order.departure_time as string | undefined) || '',
    destination_place: order.destination_place || '',
    purpose: order.purpose || '',
    actual_return_date: sliceDateForInput(order.actual_return_date) || '',
    actual_return_time:
      sliceTimeForInput(order.actual_return_time as string | undefined) ||
      sliceTimeForInput(order.expected_return_time as string | undefined) ||
      '',
    travel_companions: order.travel_companions || '',
    transport_type: order.transport_type || '',
    estimated_total: order.estimated_total != null ? String(order.estimated_total) : '',
    advance_payment: order.advance_payment != null ? String(order.advance_payment) : '',
    advance_payment_date: sliceDateForInput(order.advance_payment_date) || '',
    advance_receipt_number: order.advance_receipt_number || '',
    doc_b_transport_text: order.doc_b_transport_text || '',
    doc_b_authorized_signature_note: order.doc_b_authorized_signature_note || '',
    doc_b_report_submitted_date: sliceDateForInput(order.doc_b_report_submitted_date) || '',
    doc_b_consent_note: order.doc_b_consent_note || '',
  })

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => {
      let next = { ...prev, [name]: value }
      if (name === 'departure_date' && value && prev.actual_return_date && prev.actual_return_date < value) {
        next = { ...next, actual_return_date: value }
      }
      return next
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      if (formData.actual_return_date.trim() && formData.departure_date.trim()) {
        if (formData.actual_return_date < formData.departure_date) {
          setError('Konec cesty nemůže být dříve než její začátek (datum).')
          setSaving(false)
          return
        }
        if (
          formData.actual_return_date === formData.departure_date &&
          formData.departure_time.trim() &&
          formData.actual_return_time.trim() &&
          compareTimeHHMM(formData.actual_return_time, formData.departure_time) < 0
        ) {
          setError('Pokud je konec cesty ve stejný den jako začátek, čas konce nemůže být dříve než čas začátku.')
          setSaving(false)
          return
        }
      }
      await updateTravelOrderDocumentB(order.id, formData)
      onSuccess()
    } catch (err: any) {
      setError(err.message || 'Chyba při ukládání dat pro Dokument B')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="completion-form">
      <h2>Úprava dat pro Dokument B (ekonom)</h2>
      <p className="field-hint">
        Tato data se použijí při generování Excelu dokumentu B.
      </p>
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="field-group">
            <label>Příjmení, jméno</label>
            <input name="employee_name" value={formData.employee_name} onChange={handleChange} required />
          </div>
          <div className="field-group">
            <label>Titul</label>
            <input name="employee_title" value={formData.employee_title} onChange={handleChange} />
          </div>

          <div className="field-group">
            <label>Bydliště</label>
            <input name="employee_address" value={formData.employee_address} onChange={handleChange} />
          </div>
          <div className="field-group">
            <label>Osobní číslo</label>
            <input name="employee_personal_number" value={formData.employee_personal_number} onChange={handleChange} />
          </div>

          <div className="field-group">
            <label>Útvar</label>
            <input name="employee_department" value={formData.employee_department} onChange={handleChange} />
          </div>
          <div className="field-group">
            <label>Telefon, linka</label>
            <input name="employee_phone" value={formData.employee_phone} onChange={handleChange} />
          </div>

          <div className="field-group">
            <label>Počátek cesty - datum</label>
            <input type="date" name="departure_date" value={formData.departure_date} onChange={handleChange} />
          </div>
          <div className="field-group">
            <label>Počátek cesty - hodina</label>
            <input type="time" name="departure_time" value={formData.departure_time} onChange={handleChange} />
          </div>

          <div className="field-group">
            <label>Místo jednání</label>
            <input name="destination_place" value={formData.destination_place} onChange={handleChange} />
          </div>
          <div className="field-group">
            <label>Konec cesty - datum</label>
            <input
              type="date"
              name="actual_return_date"
              value={formData.actual_return_date}
              onChange={handleChange}
              min={formData.departure_date || undefined}
            />
          </div>
          <div className="field-group">
            <label>Konec cesty - hodina</label>
            <input type="time" name="actual_return_time" value={formData.actual_return_time} onChange={handleChange} />
          </div>

          <div className="field-group" style={{ gridColumn: '1 / -1' }}>
            <label>Účel a průběh cesty</label>
            <textarea name="purpose" value={formData.purpose} onChange={handleChange} rows={3} />
          </div>

          <div className="field-group" style={{ gridColumn: '1 / -1' }}>
            <label>Spolucestující</label>
            <input name="travel_companions" value={formData.travel_companions} onChange={handleChange} />
          </div>

          <div className="field-group">
            <label>Určený dopravní prostředek</label>
            <input name="transport_type" value={formData.transport_type} onChange={handleChange} />
          </div>
          <div className="field-group">
            <label>Text dopravy do Dokumentu B (override)</label>
            <input name="doc_b_transport_text" value={formData.doc_b_transport_text} onChange={handleChange} />
          </div>

          <div className="field-group">
            <label>Předpokládaná částka výdajů (Kč)</label>
            <input name="estimated_total" value={formData.estimated_total} onChange={handleChange} inputMode="decimal" />
          </div>
          <div className="field-group">
            <label>Povolená záloha (Kč)</label>
            <input name="advance_payment" value={formData.advance_payment} onChange={handleChange} inputMode="decimal" />
          </div>

          <div className="field-group">
            <label>Záloha vyplacená dne</label>
            <input type="date" name="advance_payment_date" value={formData.advance_payment_date} onChange={handleChange} />
          </div>
          <div className="field-group">
            <label>Pokl. doklad číslo</label>
            <input name="advance_receipt_number" value={formData.advance_receipt_number} onChange={handleChange} />
          </div>

          <div className="field-group" style={{ gridColumn: '1 / -1' }}>
            <label>Datum a podpis pracovníka oprávněného k povolení cesty (text)</label>
            <textarea
              name="doc_b_authorized_signature_note"
              value={formData.doc_b_authorized_signature_note}
              onChange={handleChange}
              rows={2}
            />
          </div>

          <div className="field-group">
            <label>Zpráva o výsledku pracovní cesty byla podána dne</label>
            <input
              type="date"
              name="doc_b_report_submitted_date"
              value={formData.doc_b_report_submitted_date}
              onChange={handleChange}
            />
          </div>
          <div className="field-group" style={{ gridColumn: '1 / -1' }}>
            <label>Se způsobem provedení souhlasí (text)</label>
            <textarea name="doc_b_consent_note" value={formData.doc_b_consent_note} onChange={handleChange} rows={2} />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Ukládám...' : '💾 Uložit změny'}
          </button>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
            Zpět
          </button>
        </div>
      </form>
    </div>
  )
}

// ============================================
// ФОРМА VYÚČTOVÁNÍ (после возвращения)
// ============================================

interface CompleteTravelOrderFormProps {
  order: TravelOrder
  /** Vedoucí upravuje již odeslané vyúčtování před odesláním ekonomovi. */
  settlementEditor?: 'employee' | 'supervisor' | 'financist'
  onSuccess: (orderId: string, opts?: { draft?: boolean }) => void
  onCancel: () => void
}

type SettlementAttDraft = {
  key: string
  file_url: string
  original_filename: string
  description: string
  uploading: boolean
}

function newSettlementAttDraft(): SettlementAttDraft {
  const key =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `att-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
  return { key, file_url: '', original_filename: '', description: '', uploading: false }
}

function orderUsesOwnVehicle(o: TravelOrder): boolean {
  const vu = o.vehicle_used === true || o.vehicle_used === 1 || String(o.vehicle_used) === '1'
  const tt = (o.transport_type || '').toLowerCase()
  return vu || tt.includes('vlastní vozidlo')
}

function parseNoteLine(notes: string | undefined, prefix: string): string {
  const n = notes || ''
  const re = new RegExp(`^${prefix}\\s*(.+)`, 'm')
  const m = re.exec(n)
  return m ? m[1].trim() : ''
}

function meetingPlaceReturnFromNotes(notes: string | undefined): string {
  return parseNoteLine(notes, 'Místo návratu:')
}

/** Read-only zobrazení: místo jednání + volitelný návrat z poznámky řádku */
function formatMeetingPlaceWithReturn(meeting?: string | null, returnPlace?: string | null): string {
  const m = (meeting || '').trim() || '—'
  const r = (returnPlace || '').trim()
  if (!r) return m
  return `${m} (návrat: ${r})`
}

/** Jedna řádka „průběh cesty“ — zjednodušeně dle školního vzoru (jen pole, která vyplňuje zaměstnanec). */
type DailyRowDraft = {
  /** Identifikace dne v tabulce + datum odjezdu (odpovídá sloupci „Odjezd — datum“) */
  record_date: string
  departure_time: string
  arrival_time: string
  arrival_date: string
  /** Text odjezdu (odkud / popis) */
  day_route: string
  meeting_place: string
  /** Místo návratu — u 2.+ dne nebo při 2. úseku; ukládá se do notes jako „Místo návratu:“ */
  meeting_place_return: string
  transport_means: string
  distance_km: string
  fare_description: string
  fare_and_local_transport: string
  incidental_description: string
  /** Stravné: tuzemske / zahranicni volí zaměstnanec */
  stravne_kind: 'tuzemske' | 'zahranicni'
  /** Stravné: počet bezplatně poskytnutých jídel (0–3) */
  free_meals_count: 0 | 1 | 2 | 3
  /** Částka stravného (Kč) — řádek tabulky / server stravne_kc */
  stravne_kc: string
  leg2_departure_date: string
  leg2_departure_time: string
  leg2_arrival_date: string
  leg2_arrival_time: string
  leg2_fare_description: string
  leg2_fare: string
  overnight_description: string
  overnight_allowance: string
  incidental_expenses: string
  row_total: string
  attachments: SettlementAttDraft[]
}

function travelTransportSelectValue(raw: string): string {
  const v = (raw || '').trim()
  if (!v) return ''
  return TRAVEL_TRANSPORT_OPTIONS.some((o) => o.value === v) ? v : 'Jiné'
}

function leg2RowHasContent(r: DailyRowDraft): boolean {
  return (
    r.leg2_departure_date.trim() !== '' ||
    r.leg2_departure_time.trim() !== '' ||
    r.leg2_arrival_date.trim() !== '' ||
    r.leg2_arrival_time.trim() !== '' ||
    r.leg2_fare_description.trim() !== '' ||
    parseMoneyStr(r.leg2_fare) !== 0
  )
}

function emptyDailyRow(partial?: Partial<DailyRowDraft>): DailyRowDraft {
  const base: DailyRowDraft = {
    record_date: '',
    departure_time: '',
    arrival_time: '',
    arrival_date: '',
    day_route: '',
    meeting_place: '',
    meeting_place_return: '',
    transport_means: '',
    distance_km: '',
    fare_description: '',
    fare_and_local_transport: '',
    incidental_description: '',
    stravne_kind: 'tuzemske',
    free_meals_count: 0,
    stravne_kc: '',
    leg2_departure_date: '',
    leg2_departure_time: '',
    leg2_arrival_date: '',
    leg2_arrival_time: '',
    leg2_fare_description: '',
    leg2_fare: '',
    overnight_description: '',
    overnight_allowance: '',
    incidental_expenses: '',
    row_total: '',
    attachments: [],
  }
  const merged: DailyRowDraft = { ...base, ...partial }
  return merged
}

function draftRowNotesForApi(r: DailyRowDraft, rowIndex: number, skipStravne = false): string | undefined {
  const parts: string[] = []
  if (rowIndex === 0 && skipStravne) parts.push('Stravné neuplatňováno: ano')
  const t1 = r.fare_description.trim()
  if (t1) parts.push(`Jízdné a místní přeprava: ${t1}`)
  const t2 = r.incidental_description.trim()
  if (t2) parts.push(`Nutné vedlejší výdaje: ${t2}`)
  parts.push(`Stravné typ: ${r.stravne_kind === 'zahranicni' ? 'zahraniční' : 'tuzemské'}`)
  parts.push(`Stravné zdarma (jídla): ${r.free_meals_count}`)
  const t5 = r.overnight_description.trim()
  if (t5) parts.push(`Ubytování, nocležné: ${t5}`)
  const s = parts.join('\n')
  return s || undefined
}

function parseMoneyStr(s: string): number {
  const t = String(s || '').trim().replace(',', '.')
  const n = parseFloat(t)
  return Number.isFinite(n) ? n : 0
}

function stravneRateForDuration(fin: TravelFinancialSettings | null, isForeign: boolean, hours: number): number {
  if (!fin) return 0
  const h = Math.max(0, hours)
  const g = (k: keyof TravelFinancialSettings) => {
    const v = fin[k]
    return typeof v === 'number' && Number.isFinite(v) ? Math.max(0, v) : 0
  }
  if (isForeign) {
    const eurToCzk = g('eur_to_czk')
    const eur =
      h < 5
        ? g('zahranicni_stravne_eur_upto_5h')
        : h < 12
          ? g('zahranicni_stravne_eur_5_to_12h')
          : h < 18
            ? g('zahranicni_stravne_eur_12_to_18h')
            : g('zahranicni_stravne_eur_over_18h')
    return eur * eurToCzk
  }
  if (h < 5) return g('tuzemske_stravne_kc_upto_5h')
  if (h < 12) return g('tuzemske_stravne_kc_5_to_12h')
  if (h < 18) return g('tuzemske_stravne_kc_12_to_18h')
  return g('tuzemske_stravne_kc_over_18h')
}

function rowEndTimeForStravne(r: DailyRowDraft): string {
  // Pokud je 2. úsek vyplněn, návrat je v něm (u jednodenní cesty).
  const t2 = r.leg2_arrival_time.trim().slice(0, 5)
  if (t2) return t2
  return r.arrival_time.trim().slice(0, 5)
}

function rowDurationHoursByTripDays(rows: DailyRowDraft[], rowIdx: number): number {
  const filled = rows
    .map((r, i) => ({ r, i, d: r.record_date.trim() }))
    .filter((x) => x.d !== '')
    .sort((a, b) => (a.d < b.d ? -1 : a.d > b.d ? 1 : a.i - b.i))
  if (filled.length === 0) return 0
  const pos = filled.findIndex((x) => x.i === rowIdx)
  if (pos < 0) return 0
  const cur = filled[pos]!.r
  const d = filled[pos]!.d
  const startT = cur.departure_time.trim().slice(0, 5)
  const endT = rowEndTimeForStravne(cur)
  if (!d || !startT) return 0
  if (filled.length === 1) {
    if (!endT) return 0
    const a = new Date(`${d}T${startT}:00`)
    const b = new Date(`${d}T${endT}:00`)
    const ms = b.getTime() - a.getTime()
    return Number.isFinite(ms) && ms > 0 ? ms / 3600000 : 0
  }
  // Vícedenní cesta: 1. den do půlnoci, prostřední dny 24h, poslední den od půlnoci do času návratu.
  if (pos === 0) {
    const a = new Date(`${d}T${startT}:00`)
    const b = new Date(`${d}T24:00:00`)
    const ms = b.getTime() - a.getTime()
    return Number.isFinite(ms) && ms > 0 ? ms / 3600000 : 0
  }
  if (pos === filled.length - 1) {
    if (!endT) return 0
    const a = new Date(`${d}T00:00:00`)
    const b = new Date(`${d}T${endT}:00`)
    const ms = b.getTime() - a.getTime()
    return Number.isFinite(ms) && ms > 0 ? ms / 3600000 : 0
  }
  return 24
}

function stravneReductionFactor(hours: number, freeMeals: 0 | 1 | 2 | 3): number {
  const h = Math.max(0, hours)
  if (h < 5) return 0
  if (h < 12) {
    if (freeMeals >= 2) return 0
    if (freeMeals === 1) return 0.3 // krácení 70%
    return 1
  }
  if (h < 18) {
    if (freeMeals >= 3) return 0
    if (freeMeals === 2) return 0.3 // krácení 70%
    if (freeMeals === 1) return 0.65 // krácení 35%
    return 1
  }
  // nad 18 hodin
  if (freeMeals >= 3) return 0.25 // krácení 75%
  if (freeMeals === 2) return 0.5 // krácení 50%
  if (freeMeals === 1) return 0.75 // krácení 25%
  return 1
}

function stravneKcForRow(
  fin: TravelFinancialSettings | null,
  isForeign: boolean,
  hours: number,
  freeMeals: 0 | 1 | 2 | 3,
): number {
  const base = stravneRateForDuration(fin, isForeign, hours) // Kč (u zahraniční už přepočítáno z EUR)
  const factor = stravneReductionFactor(hours, freeMeals)
  const perPerson = base * factor
  // Vždy jedna osoba (náhrada za stravné řeší každý spolucestující vlastním příkazem).
  const total = perPerson
  // zaokr. na 1 Kč
  return Math.round(total)
}

function rowMoneySum(r: DailyRowDraft): number {
  const leg2 = leg2RowHasContent(r) ? parseMoneyStr(r.leg2_fare) : 0
  return (
    parseMoneyStr(r.fare_and_local_transport) +
    leg2 +
    parseMoneyStr(r.stravne_kc) +
    parseMoneyStr(r.overnight_allowance) +
    parseMoneyStr(r.incidental_expenses)
  )
}

/** Přepočet stravného podle časů všech dnů + řádkové součty (volat po změně času/dne). */
function recalcStravneAndRowTotals(
  rows: DailyRowDraft[],
  fin: TravelFinancialSettings | null,
  skipStravne = false,
): DailyRowDraft[] {
  const withStrav =
    fin == null || skipStravne
      ? rows.map((r) => ({ ...r, stravne_kc: '' }))
      : rows.map((r, rowIdx) => {
          const h = rowDurationHoursByTripDays(rows, rowIdx)
          const n = stravneKcForRow(fin, r.stravne_kind === 'zahranicni', h, r.free_meals_count)
          return { ...r, stravne_kc: n > 0 ? String(n) : '' }
        })
  return withStrav.map((r) => {
    const sum = rowMoneySum(r)
    return { ...r, row_total: sum > 0 ? sum.toFixed(2) : '' }
  })
}

function parseSkipStravneFromNotes(notes: string | undefined | null): boolean {
  const line = parseNoteLine(notes || '', 'Stravné neuplatňováno:')
  return line.toLowerCase().includes('ano')
}

/** Zjednodušené vyúčtování: jeden blok údajů → řádky dle kalendářních dnů cesty (bez 2. úseků). */
function buildSettlementDailyRows(
  order: TravelOrder,
  form: {
    actual_departure_time: string
    actual_return_date: string
    actual_return_time: string
    skip_stravne: boolean
    stravne_kind: 'tuzemske' | 'zahranicni'
    free_meals_count: 0 | 1 | 2 | 3
    transport_means: string
    fare_description: string
    fare_and_local_transport: string
    overnight_description: string
    overnight_allowance: string
    incidental_description: string
    incidental_expenses: string
  },
  attachments: SettlementAttDraft[],
  fin: TravelFinancialSettings | null,
): DailyRowDraft[] {
  const depDate = sliceDateForInput(order.departure_date)
  const retDate =
    form.actual_return_date.trim() ||
    sliceDateForInput(order.actual_return_date) ||
    sliceDateForInput(order.expected_return_date) ||
    depDate
  const dates = enumerateTripDates(depDate, retDate)
  if (dates.length === 0 || !depDate) return []

  const depTime =
    form.actual_departure_time.trim() || sliceTimeForInput(order.departure_time as string | undefined) || ''
  const retTime = form.actual_return_time.trim()
  const meeting = (order.destination_place || '').trim()
  const dayRoute = TRAVEL_ORDER_DEPARTURE_PLACE_LABEL

  const rows = dates.map((d, i) => {
    const isFirst = i === 0
    const isLast = i === dates.length - 1
    const singleDay = dates.length === 1
    return emptyDailyRow({
      record_date: d,
      departure_time: isFirst ? depTime : '00:00',
      arrival_time: singleDay ? retTime : isLast ? retTime : '23:59',
      arrival_date: isLast ? retDate : d,
      day_route: isFirst ? dayRoute : dayRoute,
      meeting_place: meeting,
      transport_means: isFirst ? form.transport_means : form.transport_means,
      fare_description: isFirst ? form.fare_description : '',
      fare_and_local_transport: isFirst ? form.fare_and_local_transport : '',
      incidental_description: isFirst ? form.incidental_description : '',
      incidental_expenses: isFirst ? form.incidental_expenses : '',
      overnight_description: isFirst ? form.overnight_description : '',
      overnight_allowance: isFirst ? form.overnight_allowance : '',
      stravne_kind: form.stravne_kind,
      free_meals_count: form.free_meals_count,
      attachments: isFirst ? attachments : [],
    })
  })

  return recalcStravneAndRowTotals(rows, fin, form.skip_stravne)
}

function enumerateTripDates(startIso: string, endIso: string): string[] {
  const a = startIso.slice(0, 10)
  const b = endIso.slice(0, 10)
  if (a.length < 10 || b.length < 10) return []
  const d0 = new Date(`${a}T12:00:00`)
  const d1 = new Date(`${b}T12:00:00`)
  if (Number.isNaN(d0.getTime()) || Number.isNaN(d1.getTime()) || d1 < d0) return []
  const out: string[] = []
  for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10))
  }
  return out
}

function draftRowToApi(r: DailyRowDraft, rowIndex: number, skipStravne = false): TravelOrderDailyRecordInput {
  const sum = rowMoneySum(r)
  return {
    record_date: r.record_date,
    departure_time: r.departure_time.trim() || undefined,
    arrival_time: r.arrival_time.trim() || undefined,
    arrival_date: r.arrival_date.trim() || undefined,
    day_route: r.day_route.trim() || undefined,
    meeting_place: r.meeting_place.trim() || undefined,
    transport_means: r.transport_means.trim() || undefined,
    distance_km: r.distance_km.trim() ? parseMoneyStr(r.distance_km) : undefined,
    fare_and_local_transport: r.fare_and_local_transport.trim() ? parseMoneyStr(r.fare_and_local_transport) : undefined,
    overnight_allowance: r.overnight_allowance.trim() ? parseMoneyStr(r.overnight_allowance) : undefined,
    incidental_expenses: r.incidental_expenses.trim() ? parseMoneyStr(r.incidental_expenses) : undefined,
    stravne_kc: r.stravne_kc.trim() ? parseMoneyStr(r.stravne_kc) : undefined,
    row_total: Math.round(sum * 100) / 100,
    notes: draftRowNotesForApi(r, rowIndex, skipStravne),
  }
}

function dailyRecordToDraft(dr: TravelOrderDailyRecord): DailyRowDraft {
  const notes = dr.notes || ''
  const kindLine = parseNoteLine(notes, 'Stravné typ:')
  const freeLine = parseNoteLine(notes, 'Stravné zdarma \\(jídla\\):')
  const kind: 'tuzemske' | 'zahranicni' =
    kindLine.toLowerCase().includes('zahr') ? 'zahranicni' : 'tuzemske'
  const freeN = parseInt((freeLine || '0').trim(), 10)
  const freeMeals = (freeN === 1 || freeN === 2 || freeN === 3 ? freeN : 0) as 0 | 1 | 2 | 3
  return emptyDailyRow({
    record_date: sliceDateForInput(dr.record_date) || '',
    departure_time: sliceTimeForInput(dr.departure_time as string | undefined) || '',
    arrival_time: sliceTimeForInput(dr.arrival_time as string | undefined) || '',
    arrival_date: sliceDateForInput(dr.arrival_date) || '',
    day_route: dr.day_route || '',
    meeting_place: dr.meeting_place || '',
    meeting_place_return: meetingPlaceReturnFromNotes(notes),
    transport_means: dr.transport_means || '',
    distance_km: dr.distance_km != null && String(dr.distance_km).trim() !== '' ? String(dr.distance_km) : '',
    fare_description: parseNoteLine(notes, 'Jízdné a místní přeprava:'),
    fare_and_local_transport:
      dr.fare_and_local_transport != null && String(dr.fare_and_local_transport).trim() !== ''
        ? String(dr.fare_and_local_transport)
        : '',
    incidental_description: parseNoteLine(notes, 'Nutné vedlejší výdaje:'),
    stravne_kind: kind,
    free_meals_count: freeMeals,
    stravne_kc: dr.stravne_kc != null && String(dr.stravne_kc).trim() !== '' ? String(dr.stravne_kc) : '',
    leg2_departure_date: sliceDateForInput(dr.leg2_departure_date) || '',
    leg2_departure_time: sliceTimeForInput(dr.leg2_departure_time as string | undefined) || '',
    leg2_arrival_date: sliceDateForInput(dr.leg2_arrival_date) || '',
    leg2_arrival_time: sliceTimeForInput(dr.leg2_arrival_time as string | undefined) || '',
    leg2_fare_description: parseNoteLine(notes, 'Jízdné 2. úsek:'),
    leg2_fare: dr.leg2_fare != null && String(dr.leg2_fare).trim() !== '' ? String(dr.leg2_fare) : '',
    overnight_description: parseNoteLine(notes, 'Ubytování, nocležné:'),
    overnight_allowance:
      dr.overnight_allowance != null && String(dr.overnight_allowance).trim() !== ''
        ? String(dr.overnight_allowance)
        : '',
    incidental_expenses:
      dr.incidental_expenses != null && String(dr.incidental_expenses).trim() !== '' ? String(dr.incidental_expenses) : '',
    row_total: dr.row_total != null ? String(dr.row_total) : '',
  })
}

/** Zaměstnanec aktivně účtuje náhradu za vlastní vozidlo (ne jen příznak vozidla v příkazu). */
function orderClaimsVehicleMileageSettlement(order: TravelOrder): boolean {
  const km = order.vehicle_km_total != null ? Number(order.vehicle_km_total) : 0
  if (km > 0) return true
  const cons = order.vehicle_consumption_l_per_100km != null ? Number(order.vehicle_consumption_l_per_100km) : 0
  if (cons > 0) return true
  if ((order.vehicle_fuel_type || '').trim()) return true
  if ((order.vehicle_insurance_policy_number || '').trim()) return true
  const rc = order.vehicle_fuel_receipt_documented
  if (rc === 0 || rc === 1 || rc === true || rc === false) return true
  const rcStr = String(rc ?? '').toLowerCase()
  return rcStr === 'ano' || rcStr === 'ne' || rcStr === '1' || rcStr === '0'
}

function vehicleReceiptDocumentedFromRaw(
  raw: number | boolean | string | null | undefined,
): 'ano' | 'ne' | '' {
  if (raw === 1 || raw === true || String(raw).toLowerCase() === 'ano' || String(raw) === '1') return 'ano'
  if (raw === 0 || raw === false || String(raw).toLowerCase() === 'ne' || String(raw) === '0') return 'ne'
  return ''
}

/** Povinná pole při účtování náhrady za soukromé vozidlo — platí pro koncept i finální uložení. */
function validateVehicleSettlementFields(
  input: {
    vehicle_type: string
    vehicle_registration: string
    vehicle_insurance_policy_number: string
    vehicle_km_total: string | number | null | undefined
    vehicle_consumption_l_per_100km: string | number | null | undefined
    vehicle_fuel_select: string
    vehicle_fuel_receipt_documented: string | number | boolean | null | undefined
    vehicle_fuel_price_per_liter: string | number | null | undefined
  },
  opts: {
    financialSettings: TravelFinancialSettings | null
    hasSettlementAttachment: boolean
    financialSettingsPending?: boolean
    financialSettingsLoadError?: string | null
  },
): string | null {
  if (!(input.vehicle_type || '').trim()) {
    return 'Vyplňte použité vozidlo (značka, typ).'
  }
  if (!(input.vehicle_registration || '').trim()) {
    return 'Vyplňte SPZ.'
  }
  if (!(input.vehicle_insurance_policy_number || '').trim()) {
    return 'Vyplňte číslo havarijní pojistky.'
  }
  const kmTotal =
    typeof input.vehicle_km_total === 'number'
      ? input.vehicle_km_total
      : parseMoneyStr(String(input.vehicle_km_total ?? ''))
  if (!kmTotal || kmTotal <= 0) {
    return 'Vyplňte vzdálenost (km).'
  }
  const cons =
    typeof input.vehicle_consumption_l_per_100km === 'number'
      ? input.vehicle_consumption_l_per_100km
      : parseMoneyStr(String(input.vehicle_consumption_l_per_100km ?? ''))
  if (!cons || cons <= 0) {
    return 'Vyplňte průměrnou spotřebu dle TP (l/100 km).'
  }
  if (!(input.vehicle_fuel_select || '').trim()) {
    return 'Vyberte druh pohonných hmot.'
  }
  const receipt =
    typeof input.vehicle_fuel_receipt_documented === 'string' &&
    (input.vehicle_fuel_receipt_documented === 'ano' || input.vehicle_fuel_receipt_documented === 'ne')
      ? input.vehicle_fuel_receipt_documented
      : vehicleReceiptDocumentedFromRaw(input.vehicle_fuel_receipt_documented)
  if (!receipt) {
    return 'Vyberte, zda je cena PHM doložena účtenkou (ano/ne).'
  }
  const kmD = Math.max(0, kmTotal)
  const calcL = cons > 0 && kmD > 0 ? (kmD * cons) / 100 : 0
  const willHavePhm = calcL > 0
  if (receipt === 'ano') {
    const price =
      typeof input.vehicle_fuel_price_per_liter === 'number'
        ? input.vehicle_fuel_price_per_liter
        : parseMoneyStr(String(input.vehicle_fuel_price_per_liter ?? ''))
    if (!price || price <= 0) {
      return 'Pokud je cena PHM doložena účtenkou, vyplňte cenu PHM (Kč / jednotka).'
    }
    if (!opts.hasSettlementAttachment) {
      return 'Přiložte účtenku tlačítkem „Přidat doklad…“.'
    }
  } else if (willHavePhm) {
    if (opts.financialSettingsPending) {
      return 'Sazby z ekonomie se ještě načítají. Počkejte prosím několik sekund a uložte znovu.'
    }
    if (!opts.financialSettings) {
      const detail = opts.financialSettingsLoadError ? ` (${opts.financialSettingsLoadError})` : ''
      return `Nepodařilo se načíst sazby z ekonomie${detail}. Obnovte stránku nebo kontaktujte správce IT.`
    }
    const p = travelFuelUnitPriceFromSettings(opts.financialSettings, (input.vehicle_fuel_select || '').trim())
    if (!p || p <= 0) {
      return 'Pro zvolený druh PHM není v nastavení ekonomie platná cena. Požádejte hlavního ekonoma o doplnění (Cestovní příkaz → Ekonom → Sazby PHM).'
    }
  }
  return null
}

/** Kontrola před odesláním vyúčtování vedoucímu — stejná pravidla jako runSettlement(requireFull) v CompleteTravelOrderForm. */
function validateSettlementReadyForSubmit(
  order: TravelOrder,
  financialSettings: TravelFinancialSettings | null = null,
  financialSettingsLoadError: string | null = null,
): string | null {
  const dailyRecords = order.daily_records || []
  if (dailyRecords.length < 1) {
    return 'Vyplňte datum a čas odjezdu a návratu z cesty. Otevřete „Upravit vyúčtování“, doplňte údaje a uložte koncept.'
  }

  const first = dailyRecords[0]
  if (!String(first.record_date || '').trim()) {
    return 'Vyplňte datum a čas odjezdu a návratu z cesty. Otevřete „Upravit vyúčtování“, doplňte údaje a uložte koncept.'
  }

  if (!(order.report_text || '').trim()) {
    return 'Vyplňte zprávu o výsledku pracovní cesty.'
  }

  const depTime =
    sliceTimeForInput(first.departure_time as string | undefined) ||
    sliceTimeForInput(order.departure_time as string | undefined) ||
    ''
  if (!depTime) {
    return 'Vyplňte čas odjezdu.'
  }

  const retD = sliceDateForInput(order.actual_return_date)
  if (!retD) {
    return 'Vyplňte datum návratu.'
  }

  const retTime = sliceTimeForInput(order.actual_return_time as string | undefined)
  if (!retTime) {
    return 'Vyplňte čas návratu.'
  }

  if (!(first.transport_means || '').trim()) {
    return 'Vyberte použitý dopravní prostředek.'
  }

  const depD = sliceDateForInput(order.departure_date)
  if (depD && retD) {
    if (retD < depD) {
      return 'Datum návratu nemůže být dříve než datum odjezdu uvedené v příkazu.'
    }
    if (retD === depD && compareTimeHHMM(retTime, depTime) < 0) {
      return 'Pokud je návrat ve stejný den jako odjezd, čas návratu nemůže být dříve než čas odjezdu.'
    }
  }

  if (orderClaimsVehicleMileageSettlement(order)) {
    const vehErr = validateVehicleSettlementFields(
      {
        vehicle_type: order.vehicle_type || '',
        vehicle_registration: order.vehicle_registration || '',
        vehicle_insurance_policy_number: order.vehicle_insurance_policy_number || '',
        vehicle_km_total: order.vehicle_km_total,
        vehicle_consumption_l_per_100km: order.vehicle_consumption_l_per_100km,
        vehicle_fuel_select: order.vehicle_fuel_type || '',
        vehicle_fuel_receipt_documented: order.vehicle_fuel_receipt_documented,
        vehicle_fuel_price_per_liter: order.vehicle_fuel_price_per_liter,
      },
      {
        financialSettings,
        financialSettingsLoadError,
        hasSettlementAttachment: (order.settlement_attachments || []).some(
          (a) => String(a.file_url || '').trim() !== '',
        ),
      },
    )
    if (vehErr) return vehErr
  }

  return null
}

/** Tělo pro finální odeslání vyúčtování z detailu (data už uložená v DB z konceptu). Musí odpovídat polím z runSettlement v CompleteTravelOrderForm. */
function travelOrderToCompletePayload(order: TravelOrder): Parameters<typeof completeTravelOrder>[1] {
  const daily_records: TravelOrderDailyRecordInput[] = (order.daily_records || []).map((dr) => ({
    record_date: sliceDateForInput(dr.record_date),
    departure_time: dr.departure_time || undefined,
    arrival_time: dr.arrival_time || undefined,
    arrival_date: dr.arrival_date ? sliceDateForInput(dr.arrival_date) : undefined,
    day_route: dr.day_route || undefined,
    meeting_place: dr.meeting_place || undefined,
    transport_means: dr.transport_means || undefined,
    distance_km: dr.distance_km != null ? Number(dr.distance_km) : undefined,
    fare_and_local_transport: dr.fare_and_local_transport != null ? Number(dr.fare_and_local_transport) : undefined,
    overnight_allowance: dr.overnight_allowance != null ? Number(dr.overnight_allowance) : undefined,
    incidental_expenses: dr.incidental_expenses != null ? Number(dr.incidental_expenses) : undefined,
    leg2_departure_date: dr.leg2_departure_date ? sliceDateForInput(dr.leg2_departure_date) : undefined,
    leg2_departure_time: dr.leg2_departure_time || undefined,
    leg2_arrival_date: dr.leg2_arrival_date ? sliceDateForInput(dr.leg2_arrival_date) : undefined,
    leg2_arrival_time: dr.leg2_arrival_time || undefined,
    leg2_fare: dr.leg2_fare != null ? Number(dr.leg2_fare) : undefined,
    stravne_kc: dr.stravne_kc != null ? Number(dr.stravne_kc) : undefined,
    row_total: dr.row_total != null ? Number(dr.row_total) : undefined,
    notes: dr.notes || undefined,
  }))
  const claimMileage = Boolean(
    order.vehicle_used === true || order.vehicle_used === 1 || String(order.vehicle_used) === '1',
  )
  const fuelSelectRaw = (order.vehicle_fuel_type || '').trim()
  const receiptRaw = order.vehicle_fuel_receipt_documented
  const receiptYes =
    receiptRaw === 1 ||
    receiptRaw === true ||
    String(receiptRaw).toLowerCase() === 'ano' ||
    String(receiptRaw) === '1'
  const kmTotalNum =
    order.vehicle_km_total != null && String(order.vehicle_km_total).trim() !== ''
      ? Number(order.vehicle_km_total)
      : undefined

  // Přílohy NEposíláme při finálním „Odeslat vedoucímu“ — zůstávají ty uložené v konceptu.
  // Dříve se sem posílaly bez record_date / s prázdným polem a server je smazal → ekonom je neviděl.
  return {
    actual_return_date: sliceDateForInput(order.actual_return_date) || undefined,
    actual_return_time: sliceTimeForInput(order.actual_return_time as string | undefined) || undefined,
    vehicle_used: claimMileage,
    vehicle_type: order.vehicle_type || undefined,
    vehicle_registration: order.vehicle_registration || undefined,
    vehicle_insurance_policy_number: order.vehicle_insurance_policy_number || undefined,
    vehicle_km_start: order.vehicle_km_start != null ? Number(order.vehicle_km_start) : undefined,
    vehicle_km_end: order.vehicle_km_end != null ? Number(order.vehicle_km_end) : undefined,
    vehicle_km_total: claimMileage ? kmTotalNum : undefined,
    vehicle_consumption_l_per_100km:
      order.vehicle_consumption_l_per_100km != null ? Number(order.vehicle_consumption_l_per_100km) : undefined,
    vehicle_fuel_type: claimMileage ? fuelSelectRaw || undefined : undefined,
    vehicle_fuel_type_custom: undefined,
    vehicle_fuel_select: claimMileage ? fuelSelectRaw || undefined : undefined,
    vehicle_fuel_receipt_documented: claimMileage ? (receiptYes ? 1 : 0) : undefined,
    vehicle_fuel_price_per_liter:
      claimMileage && receiptYes && order.vehicle_fuel_price_per_liter != null
        ? Number(order.vehicle_fuel_price_per_liter)
        : undefined,
    vehicle_fuel_liters: order.vehicle_fuel_liters != null ? Number(order.vehicle_fuel_liters) : undefined,
    report_text: order.report_text || undefined,
    daily_records,
    settlement_save_only: false,
  }
}

function travelFuelUnitPriceFromSettings(
  fin: TravelFinancialSettings | null,
  fuelSelect: string
): number {
  if (!fin) return 0
  const sel = fuelSelect.trim()
  if (sel === 'benzín 95') return fin.fuel_benzin_95_kc_per_l
  if (sel === 'benzín 98') return fin.fuel_benzin_98_kc_per_l
  if (sel === 'nafta') return fin.fuel_nafta_kc_per_l
  if (sel === 'elektřina (kWh)') return fin.fuel_elektrina_kc_per_kwh
  return 0
}

function CompleteTravelOrderForm({
  order,
  settlementEditor = 'employee',
  onSuccess,
  onCancel,
}: CompleteTravelOrderFormProps) {
  const { showAlert } = useAlert()
  const [loading, setLoading] = useState(false)

  const showSettlementError = (message: string) => {
    showAlert(message, 'error', 'Vyúčtování nelze uložit')
  }

  const [dailyRows, setDailyRows] = useState<DailyRowDraft[]>([])
  const [financialSettings, setFinancialSettings] = useState<TravelFinancialSettings | null>(null)
  const [financialSettingsLoading, setFinancialSettingsLoading] = useState(true)
  const [financialSettingsLoadError, setFinancialSettingsLoadError] = useState<string | null>(null)
  const [settlementAttachments, setSettlementAttachments] = useState<SettlementAttDraft[]>([])

  const [formData, setFormData] = useState({
    actual_departure_time: '',
    actual_return_date: '',
    actual_return_time: '',
    skip_stravne: false,
    stravne_kind: 'tuzemske' as 'tuzemske' | 'zahranicni',
    free_meals_count: 0 as 0 | 1 | 2 | 3,
    transport_means: '',
    fare_description: '',
    fare_and_local_transport: '',
    overnight_description: '',
    overnight_allowance: '',
    incidental_description: '',
    incidental_expenses: '',
    vehicle_used: false,
    vehicle_type: '',
    vehicle_registration: '',
    vehicle_insurance_policy_number: '',
    vehicle_km_total: '',
    vehicle_consumption_l_per_100km: '',
    vehicle_fuel_select: '',
    vehicle_fuel_receipt_documented: '',
    vehicle_fuel_price_per_liter: '',
    report_text: '',
  })

  useEffect(() => {
    let cancelled = false
    setFinancialSettingsLoading(true)
    setFinancialSettingsLoadError(null)
    loadTravelFinancialSettings()
      .then((s) => {
        if (!cancelled) {
          setFinancialSettings(s)
          setFinancialSettingsLoadError(null)
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setFinancialSettings(null)
          setFinancialSettingsLoadError(err.message || 'Chyba načtení sazeb')
        }
      })
      .finally(() => {
        if (!cancelled) setFinancialSettingsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const ensureFinancialSettings = async (): Promise<{
    settings: TravelFinancialSettings | null
    error: string | null
  }> => {
    if (financialSettings) return { settings: financialSettings, error: null }
    try {
      const s = await loadTravelFinancialSettings()
      setFinancialSettings(s)
      setFinancialSettingsLoadError(null)
      return { settings: s, error: null }
    } catch (err: any) {
      const msg = err.message || 'Chyba načtení sazeb'
      setFinancialSettingsLoadError(msg)
      return { settings: null, error: msg }
    }
  }

  useEffect(() => {
    const own = orderUsesOwnVehicle(order)
    const savedClaim =
      order.vehicle_used === true ||
      order.vehicle_used === 1 ||
      String(order.vehicle_used) === '1'
    const firstDr = order.daily_records?.[0]
    const firstDraft = firstDr ? dailyRecordToDraft(firstDr) : null
    const skipStravne =
      order.daily_records?.some((dr) => parseSkipStravneFromNotes(dr.notes)) ?? false

    setSettlementAttachments(
      (order.settlement_attachments || []).map((a) => ({
        key: a.id || `att-${a.file_url}`,
        file_url: a.file_url,
        original_filename: a.original_filename || '',
        description: (a.description as string) || '',
        uploading: false,
      })),
    )

    setFormData({
      actual_departure_time:
        firstDraft?.departure_time ||
        sliceTimeForInput(order.departure_time as string | undefined) ||
        '',
      actual_return_date:
        sliceDateForInput(order.actual_return_date) || sliceDateForInput(order.expected_return_date) || '',
      actual_return_time:
        sliceTimeForInput(order.actual_return_time as string | undefined) ||
        sliceTimeForInput(order.expected_return_time as string | undefined) ||
        '',
      skip_stravne: skipStravne,
      stravne_kind: firstDraft?.stravne_kind || 'tuzemske',
      free_meals_count: firstDraft?.free_meals_count ?? 0,
      transport_means: firstDraft?.transport_means || order.transport_type || '',
      fare_description: firstDraft?.fare_description || '',
      fare_and_local_transport: firstDraft?.fare_and_local_transport || '',
      overnight_description: firstDraft?.overnight_description || '',
      overnight_allowance: firstDraft?.overnight_allowance || '',
      incidental_description: firstDraft?.incidental_description || '',
      incidental_expenses: firstDraft?.incidental_expenses || '',
      vehicle_used: own && savedClaim,
      vehicle_type: order.vehicle_type || '',
      vehicle_registration: order.vehicle_registration || '',
      vehicle_insurance_policy_number:
        order.vehicle_insurance_policy_number != null ? String(order.vehicle_insurance_policy_number) : '',
      vehicle_km_total: order.vehicle_km_total != null ? String(order.vehicle_km_total) : '',
      vehicle_consumption_l_per_100km:
        order.vehicle_consumption_l_per_100km != null ? String(order.vehicle_consumption_l_per_100km) : '',
      vehicle_fuel_select: order.vehicle_fuel_type || '',
      vehicle_fuel_receipt_documented:
        order.vehicle_fuel_receipt_documented != null && String(order.vehicle_fuel_receipt_documented) !== ''
          ? String(order.vehicle_fuel_receipt_documented) === '1' ||
            String(order.vehicle_fuel_receipt_documented).toLowerCase() === 'ano'
            ? 'ano'
            : 'ne'
          : '',
      vehicle_fuel_price_per_liter:
        order.vehicle_fuel_price_per_liter != null ? String(order.vehicle_fuel_price_per_liter) : '',
      report_text: order.report_text || '',
    })
  }, [order])

  useEffect(() => {
    setDailyRows(
      buildSettlementDailyRows(order, formData, settlementAttachments, financialSettings),
    )
  }, [
    order,
    financialSettings,
    settlementAttachments,
    formData.actual_departure_time,
    formData.actual_return_date,
    formData.actual_return_time,
    formData.skip_stravne,
    formData.stravne_kind,
    formData.free_meals_count,
    formData.transport_means,
    formData.fare_description,
    formData.fare_and_local_transport,
    formData.overnight_description,
    formData.overnight_allowance,
    formData.incidental_description,
    formData.incidental_expenses,
  ])

  const parseMoney = (s: string) => parseMoneyStr(s)

  const sumDailyRows = dailyRows.reduce((acc, r) => acc + rowMoneySum(r), 0)
  const tripStartDate = sliceDateForInput(order.departure_date)

  const addSettlementAttachment = () => {
    if (settlementAttachments.length >= 40) return
    setSettlementAttachments((prev) => [...prev, newSettlementAttDraft()])
  }

  const removeSettlementAttachment = (key: string) => {
    setSettlementAttachments((prev) => prev.filter((a) => a.key !== key))
  }

  const updateSettlementAttachmentDesc = (key: string, description: string) => {
    setSettlementAttachments((prev) => prev.map((a) => (a.key === key ? { ...a, description } : a)))
  }

  const onSettlementAttachmentFile = async (key: string, fileList: FileList | null) => {
    const file = fileList?.[0]
    if (!file) return
    setSettlementAttachments((prev) =>
      prev.map((a) => (a.key === key ? { ...a, uploading: true } : a)),
    )
    try {
      const r = await uploadFile(file)
      setSettlementAttachments((prev) =>
        prev.map((a) =>
          a.key === key
            ? { ...a, uploading: false, file_url: r.file_url, original_filename: r.file_name }
            : a,
        ),
      )
    } catch (err: any) {
      setSettlementAttachments((prev) =>
        prev.map((a) => (a.key === key ? { ...a, uploading: false } : a)),
      )
      showSettlementError(err.message || 'Chyba při nahrávání souboru')
    }
  }

  const runSettlement = async (saveDraft: boolean) => {
    setLoading(true)
    const requireFull = !saveDraft

    try {
      const filledRows = dailyRows.filter((r) => r.record_date.trim() !== '')
      if (filledRows.length < 1) {
        showSettlementError('Vyplňte datum a čas odjezdu a návratu z cesty.')
        setLoading(false)
        return
      }

      if (requireFull && !formData.report_text.trim()) {
        showSettlementError('Vyplňte zprávu o výsledku pracovní cesty.')
        setLoading(false)
        return
      }

      const depD = sliceDateForInput(order.departure_date)
      const retD = formData.actual_return_date.trim()

      if (requireFull) {
        if (!formData.actual_departure_time.trim()) {
          showSettlementError('Vyplňte čas odjezdu.')
          setLoading(false)
          return
        }
        if (!retD) {
          showSettlementError('Vyplňte datum návratu.')
          setLoading(false)
          return
        }
        if (!formData.actual_return_time.trim()) {
          showSettlementError('Vyplňte čas návratu.')
          setLoading(false)
          return
        }
        if (!formData.transport_means.trim()) {
          showSettlementError('Vyberte použitý dopravní prostředek.')
          setLoading(false)
          return
        }
      }

      if (requireFull && retD && depD) {
        if (retD < depD) {
          showSettlementError('Datum návratu nemůže být dříve než datum odjezdu uvedené v příkazu.')
          setLoading(false)
          return
        }
        if (
          retD === depD &&
          formData.actual_departure_time.trim() &&
          formData.actual_return_time.trim() &&
          compareTimeHHMM(formData.actual_return_time, formData.actual_departure_time) < 0
        ) {
          showSettlementError('Pokud je návrat ve stejný den jako odjezd, čas návratu nemůže být dříve než čas odjezdu.')
          setLoading(false)
          return
        }
      }

      if (settlementAttachments.some((a) => a.uploading)) {
        showSettlementError('Počkejte na dokončení nahrávání příloh.')
        setLoading(false)
        return
      }

      const claimMileage = formData.vehicle_used
      if (claimMileage) {
        let finForValidation = financialSettings
        let finLoadError = financialSettingsLoadError
        const receiptIsNe = formData.vehicle_fuel_receipt_documented.trim() === 'ne'
        if (receiptIsNe && !finForValidation) {
          if (financialSettingsLoading) {
            showSettlementError('Sazby z ekonomie se ještě načítají. Počkejte prosím několik sekund a uložte znovu.')
            setLoading(false)
            return
          }
          const finReload = await ensureFinancialSettings()
          finForValidation = finReload.settings
          finLoadError = finReload.error
        }
        const vehErr = validateVehicleSettlementFields(
          {
            vehicle_type: formData.vehicle_type,
            vehicle_registration: formData.vehicle_registration,
            vehicle_insurance_policy_number: formData.vehicle_insurance_policy_number,
            vehicle_km_total: formData.vehicle_km_total,
            vehicle_consumption_l_per_100km: formData.vehicle_consumption_l_per_100km,
            vehicle_fuel_select: formData.vehicle_fuel_select,
            vehicle_fuel_receipt_documented: formData.vehicle_fuel_receipt_documented,
            vehicle_fuel_price_per_liter: formData.vehicle_fuel_price_per_liter,
          },
          {
            financialSettings: finForValidation,
            financialSettingsPending: financialSettingsLoading,
            financialSettingsLoadError: finLoadError,
            hasSettlementAttachment: settlementAttachments.some((a) => String(a.file_url || '').trim() !== ''),
          },
        )
        if (vehErr) {
          showSettlementError(vehErr)
          setLoading(false)
          return
        }
      }

      const vehicle_km_total = claimMileage && formData.vehicle_km_total.trim() ? parseMoney(formData.vehicle_km_total) : 0

      const daily_records = dailyRows
        .map((r, idx) => ({ r, idx }))
        .filter(({ r }) => r.record_date.trim() !== '')
        .map(({ r, idx }) => draftRowToApi(r, idx, formData.skip_stravne))

      const fuelTypeApi = !claimMileage ? undefined : formData.vehicle_fuel_select.trim() || undefined
      const fuelCustomApi = undefined

      const attachmentRecordDate = tripStartDate || filledRows[0]?.record_date.trim() || ''
      const settlementAttachmentsPayload: {
        file_url: string
        original_filename?: string
        description?: string
        record_date: string
      }[] = []
      for (const att of settlementAttachments) {
        if (!att.file_url.trim()) continue
        if (!attachmentRecordDate) {
          showSettlementError('Nelze uložit přílohy — chybí datum odjezdu v příkazu.')
          setLoading(false)
          return
        }
        settlementAttachmentsPayload.push({
          file_url: att.file_url.trim(),
          original_filename: att.original_filename.trim() || undefined,
          description: att.description.trim() || undefined,
          record_date: attachmentRecordDate,
        })
      }

      await completeTravelOrder(order.id, {
        actual_return_date: formData.actual_return_date || undefined,
        actual_return_time: formData.actual_return_time || undefined,
        vehicle_used: claimMileage,
        vehicle_type: claimMileage ? formData.vehicle_type.trim() || undefined : undefined,
        vehicle_registration: claimMileage ? formData.vehicle_registration.trim() || undefined : undefined,
        vehicle_insurance_policy_number: claimMileage ? formData.vehicle_insurance_policy_number.trim() : undefined,
        vehicle_km_start: undefined,
        vehicle_km_end: undefined,
        vehicle_km_total: claimMileage ? vehicle_km_total : undefined,
        vehicle_consumption_l_per_100km:
          claimMileage && formData.vehicle_consumption_l_per_100km.trim()
            ? parseMoney(formData.vehicle_consumption_l_per_100km)
            : undefined,
        vehicle_fuel_type: fuelTypeApi,
        vehicle_fuel_type_custom: fuelCustomApi,
        vehicle_fuel_select: claimMileage ? formData.vehicle_fuel_select.trim() || undefined : undefined,
        vehicle_fuel_receipt_documented: claimMileage ? (formData.vehicle_fuel_receipt_documented.trim() === 'ano' ? 1 : 0) : undefined,
        vehicle_fuel_price_per_liter:
          claimMileage && formData.vehicle_fuel_receipt_documented.trim() === 'ano'
            ? parseMoney(formData.vehicle_fuel_price_per_liter)
            : pplFromSettings || undefined,
        vehicle_fuel_liters: undefined,
        report_text: formData.report_text.trim() || undefined,
        daily_records,
        settlement_attachments: settlementAttachmentsPayload,
        settlement_save_only: saveDraft,
      })

      onSuccess(order.id, { draft: saveDraft })
    } catch (err: any) {
      showSettlementError(err.message || 'Chyba při vyúčtování')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const t = e.target
    const name = t.name
    const type = (t as HTMLInputElement).type
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: (t as HTMLInputElement).checked }))
      return
    }
    if (type === 'radio') {
      setFormData((prev) => ({ ...prev, [name]: (t as HTMLInputElement).value }))
      return
    }
    setFormData((prev) => ({ ...prev, [name]: t.value }))
  }

  const depD = sliceDateForInput(order.departure_date)
  const tripDaysCount = dailyRows.length

  const orderOwnVehicle = orderUsesOwnVehicle(order)
  const claimMileage = orderOwnVehicle && formData.vehicle_used
  const kmPreview = claimMileage && formData.vehicle_km_total.trim() ? Math.max(0, parseMoney(formData.vehicle_km_total)) : 0
  const kmRatePreview = financialSettings?.mileage_compensation_kc_per_km ?? 11
  const kmPartPreview = claimMileage ? kmPreview * kmRatePreview : 0
  const consPreview =
    claimMileage && formData.vehicle_consumption_l_per_100km.trim()
      ? parseMoney(formData.vehicle_consumption_l_per_100km)
      : 0
  const calcLitersPreview =
    claimMileage && kmPreview > 0 && consPreview > 0 ? (kmPreview * consPreview) / 100 : 0
  const pplFromSettings = travelFuelUnitPriceFromSettings(
    financialSettings,
    formData.vehicle_fuel_select
  )
  const pplEffective =
    claimMileage && formData.vehicle_fuel_receipt_documented === 'ano' && formData.vehicle_fuel_price_per_liter.trim()
      ? parseMoney(formData.vehicle_fuel_price_per_liter)
      : pplFromSettings
  const litersForFuelPreview = calcLitersPreview
  const fuelPreview =
    claimMileage && pplEffective > 0 && litersForFuelPreview > 0
      ? litersForFuelPreview * pplEffective
      : 0
  const tableSumPreview = sumDailyRows + kmPartPreview + fuelPreview
  const sumFareOnly = dailyRows.reduce((acc, r) => acc + parseMoneyStr(r.fare_and_local_transport), 0)
  const sumNocOnly = dailyRows.reduce((acc, r) => acc + parseMoneyStr(r.overnight_allowance), 0)
  const sumIncOnly = dailyRows.reduce((acc, r) => acc + parseMoneyStr(r.incidental_expenses), 0)
  const sumStravOnly = formData.skip_stravne
    ? 0
    : dailyRows.reduce((acc, r) => acc + parseMoneyStr(r.stravne_kc), 0)
  const stravnePreviewKc = formData.skip_stravne ? 0 : sumStravOnly
  const advanceNum = order.advance_payment != null ? Number(order.advance_payment) : 0
  const settlementAdvancePreview = tableSumPreview - advanceNum
  const isSupervisorEditor = settlementEditor === 'supervisor'
  const isFinancistEditor = settlementEditor === 'financist'

  return (
    <form
      className="travel-order-form travel-order-settlement-form"
      onSubmit={(e) => {
        e.preventDefault()
        void runSettlement(false)
      }}
    >
      <h2>VYÚČTOVÁNÍ PRACOVNÍ CESTY — příkaz #{order.order_number}</h2>
      {isSupervisorEditor && (
        <p className="field-hint" style={{ marginTop: 0 }}>
          Upravujete vyúčtování jako vedoucí. Po uložení konceptu zůstane příkaz u vás k dokončení; tlačítkem
          „Potvrdit a odeslat ekonomovi“ odešlete data hlavnímu ekonomovi.
        </p>
      )}
      {isFinancistEditor && (
        <p className="field-hint" style={{ marginTop: 0 }}>
          Upravujete vyúčtování jako ekonom. Po uložení se pouze přepočítají a uloží data; stav příkazu zůstane zachován.
        </p>
      )}

      <div className="form-section travel-order-settlement-section">
        <h3>Zpráva o výsledku pracovní cesty *</h3>
        <p className="field-hint">Stručný text popisující cestu, případně naplnění jejich cílů. Povinné při dokončení vyúčtování.</p>
        <div className="form-group">
          <textarea name="report_text" value={formData.report_text} onChange={handleChange} rows={5} placeholder="Text zprávy…" />
        </div>
      </div>

      <div className="form-section travel-order-settlement-section">
        <h3>Období cesty</h3>
        <p className="field-hint">
          Vyplňte skutečný čas odjezdu a návratu. Místo odjezdu je vždy {TRAVEL_ORDER_DEPARTURE_PLACE_LABEL}, místo
          jednání: <strong>{order.destination_place || '—'}</strong>.
          {tripDaysCount > 1 && (
            <>
              {' '}
              Cesta trvá <strong>{tripDaysCount} kalendářní dny</strong> — stravné se dopočítá automaticky podle délky
              jednotlivých dnů.
            </>
          )}
        </p>
        <div className="form-grid">
          <div className="form-group">
            <label>Odjezd — datum</label>
            <input type="date" value={depD} disabled />
          </div>
          <div className="form-group">
            <label>Odjezd — čas *</label>
            <input
              type="time"
              name="actual_departure_time"
              value={formData.actual_departure_time}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Návrat — datum *</label>
            <input
              type="date"
              name="actual_return_date"
              value={formData.actual_return_date}
              onChange={handleChange}
              min={depD || undefined}
            />
          </div>
          <div className="form-group">
            <label>Návrat — čas *</label>
            <input type="time" name="actual_return_time" value={formData.actual_return_time} onChange={handleChange} />
          </div>
        </div>
      </div>

      <div className="form-section travel-order-settlement-section">
        <h3>Náklady cesty</h3>
        <div className="form-grid">
          <div className="form-group form-group--wide">
            <label>Použitý dopravní prostředek *</label>
            <select
              value={travelTransportSelectValue(formData.transport_means)}
              onChange={(e) => {
                const v = e.target.value
                if (v === 'Jiné') {
                  setFormData((p) => ({ ...p, transport_means: '' }))
                  return
                }
                setFormData((p) => ({
                  ...p,
                  transport_means: v,
                  vehicle_used: v === 'Vlastní vozidlo' ? true : p.vehicle_used,
                }))
              }}
            >
              <option value="">Vyberte…</option>
              {TRAVEL_TRANSPORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {travelTransportSelectValue(formData.transport_means) === 'Jiné' && (
              <div style={{ marginTop: 8 }}>
                <input
                  type="text"
                  name="transport_means"
                  value={formData.transport_means}
                  onChange={handleChange}
                  placeholder="Konkrétní dopravní prostředek…"
                />
              </div>
            )}
          </div>
          <div className="form-group form-group--wide">
            <label>Jízdné a místní přeprava (text)</label>
            <textarea
              rows={2}
              name="fare_description"
              value={formData.fare_description}
              onChange={handleChange}
              placeholder="popis / číslo spoje…"
            />
          </div>
          <div className="form-group">
            <label>Jízdné — suma (Kč)</label>
            <input
              type="text"
              inputMode="decimal"
              name="fare_and_local_transport"
              value={formData.fare_and_local_transport}
              onChange={handleChange}
              placeholder="0"
            />
          </div>
          <div className="form-group form-group--wide">
            <label>Ubytování, nocležné (text)</label>
            <input
              type="text"
              name="overnight_description"
              value={formData.overnight_description}
              onChange={handleChange}
              placeholder="volitelný popis"
            />
          </div>
          <div className="form-group">
            <label>Ubytování — suma (Kč)</label>
            <input
              type="text"
              inputMode="decimal"
              name="overnight_allowance"
              value={formData.overnight_allowance}
              onChange={handleChange}
              placeholder="0"
            />
          </div>
          <div className="form-group form-group--wide">
            <label>Nutné vedlejší výdaje (text)</label>
            <textarea
              rows={2}
              name="incidental_description"
              value={formData.incidental_description}
              onChange={handleChange}
              placeholder="popis vedlejších výdajů"
            />
          </div>
          <div className="form-group">
            <label>Vedlejší výdaje — suma (Kč)</label>
            <input
              type="text"
              inputMode="decimal"
              name="incidental_expenses"
              value={formData.incidental_expenses}
              onChange={handleChange}
              placeholder="0"
            />
          </div>
        </div>
      </div>

      <div className="form-section travel-order-settlement-section">
        <h3>Stravné</h3>
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="skip_stravne"
              checked={formData.skip_stravne}
              onChange={handleChange}
            />
            Neuplatňuji náhradu za stravné (nemám na ni nárok nebo ji nechci čerpat)
          </label>
        </div>
        {!formData.skip_stravne && (
          <div className="form-grid" style={{ marginTop: 8 }}>
            <div className="form-group">
              <label>Typ stravného</label>
              <select
                name="stravne_kind"
                value={formData.stravne_kind}
                onChange={handleChange}
              >
                <option value="tuzemske">Tuzemské</option>
                <option value="zahranicni">Zahraniční</option>
              </select>
            </div>
            <div className="form-group">
              <label>Bezplatně poskytnutá jídla (0–3)</label>
              <select
                name="free_meals_count"
                value={String(formData.free_meals_count)}
                onChange={(e) => {
                  const c0 = parseInt(e.target.value, 10)
                  const c = (c0 === 1 || c0 === 2 || c0 === 3 ? c0 : 0) as 0 | 1 | 2 | 3
                  setFormData((p) => ({ ...p, free_meals_count: c }))
                }}
              >
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </select>
            </div>
            <div className="form-group">
              <label>Vypočtená částka (Kč)</label>
              <input type="text" value={stravnePreviewKc > 0 ? String(stravnePreviewKc) : '0'} disabled />
            </div>
          </div>
        )}
        <p className="field-hint" style={{ marginTop: 8 }}>
          Stravné se počítá podle délky cesty a sazeb nastavených ekonomem. Zaškrtněte volbu výše, pokud na stravné nemáte
          nárok nebo ho nechcete uplatnit.
        </p>
      </div>

      <div className="form-section travel-order-settlement-section">
        <h3>Doklady (volitelné)</h3>
        <p className="field-hint">Účtenky a další doklady (max. 40 souborů). Vztahují se k datu odjezdu cesty.</p>
        <div className="travel-order-settlement-attachments">
          {settlementAttachments.map((att) => (
            <div key={att.key} className="travel-order-attachment-row">
              <div className="form-group travel-order-attachment-row__desc">
                <label>Popis (volitelné)</label>
                <input
                  type="text"
                  value={att.description}
                  placeholder="např. účtenka hotel"
                  disabled={att.uploading || loading}
                  onChange={(e) => updateSettlementAttachmentDesc(att.key, e.target.value)}
                />
              </div>
              <div className="form-group travel-order-attachment-row__file">
                <label>Soubor</label>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,application/pdf,image/*"
                  disabled={att.uploading || loading}
                  onChange={(e) => {
                    const fl = e.target.files
                    void onSettlementAttachmentFile(att.key, fl)
                    e.target.value = ''
                  }}
                />
                {att.uploading && <span className="field-hint">Nahrávám…</span>}
                {att.file_url && !att.uploading && (
                  <p className="field-hint">
                    Nahráno:{' '}
                    <a href={protectedFileHref(att.file_url)} target="_blank" rel="noopener noreferrer">
                      {att.original_filename || 'soubor'}
                    </a>
                  </p>
                )}
              </div>
              <div className="travel-order-attachment-row__actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => removeSettlementAttachment(att.key)}
                  disabled={att.uploading || loading}
                >
                  Odebrat
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="btn-secondary"
            onClick={addSettlementAttachment}
            disabled={loading || settlementAttachments.length >= 40}
          >
            Přidat doklad…
          </button>
        </div>
      </div>

      {(orderOwnVehicle || formData.vehicle_used) && (
      <div className="form-section travel-order-settlement-section">
        <h3>Vyúčtování cestovních náhrad za používání soukromého automobilu</h3>
        <p className="field-hint">
          V příkazu je uvedeno vlastní vozidlo — údaje se předvyplní. Náhrada za km a cena PHM se berou ze{' '}
          <strong>sazebníku nastaveného hlavním ekonomem</strong> (záložka Ekonom → blok „Sazby PHM a náhrady“).
          Při zaškrtnutí účtování náhrady jsou všechna pole níže povinná.
        </p>
        <div className="form-group">
          <label className="checkbox-label">
            <input type="checkbox" name="vehicle_used" checked={formData.vehicle_used} onChange={handleChange} />
            Účtuji náhradu za použití soukromého motorového vozidla
          </label>
        </div>

        {formData.vehicle_used && (
          <>
            <div className="form-grid">
              <div className="form-group form-group--wide">
                <label>Použité vozidlo, značka, typ *</label>
                <input
                  type="text"
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleChange}
                  required={formData.vehicle_used}
                />
              </div>
              <div className="form-group">
                <label>SPZ *</label>
                <input
                  type="text"
                  name="vehicle_registration"
                  value={formData.vehicle_registration}
                  onChange={handleChange}
                  required={formData.vehicle_used}
                />
              </div>
              <div className="form-group form-group--wide">
                <label>Číslo havarijní pojistky *</label>
                <input
                  type="text"
                  name="vehicle_insurance_policy_number"
                  value={formData.vehicle_insurance_policy_number}
                  onChange={handleChange}
                  required={formData.vehicle_used}
                />
              </div>
              <div className="form-group">
                <label>Vzdálenost (km) *</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="vehicle_km_total"
                  value={formData.vehicle_km_total}
                  onChange={handleChange}
                  placeholder="např. 120"
                  required={formData.vehicle_used}
                />
              </div>
              <div className="form-group">
                <label>Průměrná spotřeba dle TP (l/100 km) *</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="vehicle_consumption_l_per_100km"
                  value={formData.vehicle_consumption_l_per_100km}
                  onChange={handleChange}
                  required={formData.vehicle_used}
                />
              </div>
              <div className="form-group">
                <label>Druh pohonných hmot *</label>
                <select
                  name="vehicle_fuel_select"
                  value={formData.vehicle_fuel_select}
                  onChange={handleChange}
                  required={formData.vehicle_used}
                >
                  <option value="">— vyberte —</option>
                  <option value="benzín 95">benzín 95</option>
                  <option value="benzín 98">benzín 98</option>
                  <option value="nafta">nafta</option>
                  <option value="elektřina (kWh)">elektřina (kWh)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Cena PHM doložena účtenkou *</label>
                <select
                  name="vehicle_fuel_receipt_documented"
                  value={formData.vehicle_fuel_receipt_documented}
                  onChange={handleChange}
                  required={formData.vehicle_used}
                >
                  <option value="">— vyberte —</option>
                  <option value="ano">ano</option>
                  <option value="ne">ne</option>
                </select>
                {formData.vehicle_fuel_receipt_documented === 'ano' && (
                  <div className="field-hint" style={{ marginTop: 6 }}>
                    Přiložte účtenku v sekci Doklady výše.
                  </div>
                )}
              </div>
              {formData.vehicle_fuel_receipt_documented === 'ano' && (
                <div className="form-group">
                  <label>Cena PHM (Kč / jednotka) *</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    name="vehicle_fuel_price_per_liter"
                    value={formData.vehicle_fuel_price_per_liter}
                    onChange={handleChange}
                    placeholder="např. 33,3"
                    required={formData.vehicle_used}
                  />
                </div>
              )}
              <div className="form-group form-group--wide travel-order-rates-readonly">
                <label>Aktuální sazby (z nastavení ekonomie)</label>
                {financialSettingsLoading && <p className="field-hint">Načítání sazeb…</p>}
                {!financialSettingsLoading && financialSettingsLoadError && (
                  <p className="field-hint" style={{ color: '#a11' }}>
                    Nepodařilo se načíst sazby: {financialSettingsLoadError}
                  </p>
                )}
                {financialSettings && (
                  <ul className="travel-order-rates-readonly-list">
                    <li>Základní sazba za 1 km: {financialSettings.mileage_compensation_kc_per_km.toFixed(2)} Kč/km</li>
                    <li>Benzín 95: {financialSettings.fuel_benzin_95_kc_per_l.toFixed(2)} Kč/l</li>
                    <li>Benzín 98: {financialSettings.fuel_benzin_98_kc_per_l.toFixed(2)} Kč/l</li>
                    <li>Nafta: {financialSettings.fuel_nafta_kc_per_l.toFixed(2)} Kč/l</li>
                    <li>Elektřina: {financialSettings.fuel_elektrina_kc_per_kwh.toFixed(2)} Kč/kWh</li>
                  </ul>
                )}
                <span className="field-hint">
                  Cena PHM pro vybraný druh: {pplFromSettings > 0 ? `${pplFromSettings.toFixed(2)} Kč/jednotka` : '— (vyberte druh PHM)'}.
                  Množství PHM se dopočítá z km a spotřeby dle TP.
                </span>
              </div>
            </div>
            <div className="travel-order-auto-calc">
              <p>
                <strong>Počet ujetých km:</strong> {kmPreview} — <strong>vypočtená spotřeba PHM:</strong>{' '}
                {calcLitersPreview > 0 ? calcLitersPreview.toFixed(2) : '—'}
              </p>
              <p>
                <strong>Náhrada za km (náhled):</strong> {kmPartPreview.toFixed(2)} Kč ({kmRatePreview.toFixed(2)} Kč/km × {kmPreview} km)
              </p>
              <p>
                <strong>Náklady na PHM (náhled):</strong> {fuelPreview.toFixed(2)} Kč
              </p>
            </div>
          </>
        )}
      </div>
      )}

      <div className="form-section travel-order-settlement-section">
        <h3>Sumář výdajů (náhled)</h3>
        <p className="field-hint">Definitivní součty dopočítá server při uložení.</p>
        <ul className="travel-order-summary-list">
          <li>Jízdné a místní přeprava: {sumFareOnly.toFixed(2)} Kč</li>
          <li>Stravné: {formData.skip_stravne ? 'neuplatňováno' : `${sumStravOnly.toFixed(2)} Kč`}</li>
          <li>Nutné vedlejší výdaje: {sumIncOnly.toFixed(2)} Kč</li>
          <li>Ubytování, nocležné: {sumNocOnly.toFixed(2)} Kč</li>
          {claimMileage && (
            <>
              <li>
                Náhrada za km (vlastní vozidlo): {kmPartPreview.toFixed(2)} Kč
                {kmPreview > 0 && (
                  <span className="field-hint">
                    {' '}
                    ({kmRatePreview.toFixed(2)} Kč/km × {kmPreview} km)
                  </span>
                )}
              </li>
              <li>
                Náklady na PHM (vlastní vozidlo): {fuelPreview.toFixed(2)} Kč
                {calcLitersPreview > 0 && (
                  <span className="field-hint">
                    {' '}
                    ({calcLitersPreview.toFixed(2)}{' '}
                    {formData.vehicle_fuel_select === 'elektřina (kWh)' ? 'kWh' : 'l'})
                  </span>
                )}
              </li>
            </>
          )}
          <li>
            <strong>Celkové výdaje k proplacení (náhled): {tableSumPreview.toFixed(2)} Kč</strong>
          </li>
          <li>Vyplacená záloha (z příkazu): {advanceNum.toFixed(2)} Kč</li>
          <li>
            <strong>Vyúčtování zálohy (náhled): {settlementAdvancePreview.toFixed(2)} Kč</strong>
            <span className="field-hint"> (celkem − záloha)</span>
          </li>
        </ul>
      </div>

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
          Zrušit
        </button>
        {!isFinancistEditor && (
          <button type="button" className="btn-secondary" disabled={loading} onClick={() => void runSettlement(true)}>
            {loading ? 'Ukládání…' : isSupervisorEditor ? 'Uložit úpravy (koncept)' : 'Uložit koncept'}
          </button>
        )}
        {(isSupervisorEditor || isFinancistEditor) && (
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Ukládání…' : isSupervisorEditor ? 'Potvrdit a odeslat ekonomovi' : 'Uložit úpravy (ekonom)'}
          </button>
        )}
      </div>
    </form>
  )
}
